(() => {
  const THREE_VERSION = '0.180.0';
  const MODULE_SOURCES = [
    {
      name: 'esm.sh',
      three: `https://esm.sh/three@${THREE_VERSION}?target=es2020`,
      controls: `https://esm.sh/three@${THREE_VERSION}/examples/jsm/controls/OrbitControls.js?target=es2020`
    },
    {
      name: 'Skypack',
      three: `https://cdn.skypack.dev/three@${THREE_VERSION}`,
      controls: `https://cdn.skypack.dev/three@${THREE_VERSION}/examples/jsm/controls/OrbitControls.js`
    }
  ];

  let dependencyPromise = null;

  async function importDependencies() {
    let lastError = null;

    for (const source of MODULE_SOURCES) {
      try {
        const [THREE, controls] = await Promise.all([
          import(source.three),
          import(source.controls)
        ]);

        if (!THREE?.WebGLRenderer || !controls?.OrbitControls) {
          throw new Error(`Invalid Three.js modules from ${source.name}`);
        }

        return {
          THREE,
          OrbitControls: controls.OrbitControls,
          source: source.name
        };
      } catch (error) {
        lastError = error;
        console.warn(`JD Kitchen Builder 3D: ${source.name} failed, trying fallback`, error);
      }
    }

    throw lastError || new Error('Unable to load Three.js');
  }

  function loadDependencies() {
    if (!dependencyPromise) {
      dependencyPromise = importDependencies().catch(error => {
        // Allow a later 2D -> 3D retry without requiring a full page refresh.
        dependencyPromise = null;
        throw error;
      });
    }
    return dependencyPromise;
  }

  class JDKitchen3D {
    constructor(container, options = {}) {
      this.container = container;
      this.options = options;
      this.THREE = null;
      this.OrbitControls = null;
      this.scene = null;
      this.camera = null;
      this.renderer = null;
      this.controls = null;
      this.modelGroup = null;
      this.selectionHelper = null;
      this.selectionFrontMarker = null;
      this.selectionFrontGuide = null;
      this.selectionMoveHandle = null;
      this.selectedUid = null;
      this.moveDrag = null;
      this.dragPlane = null;
      this.raycaster = null;
      this.pointer = null;
      this.ready = false;
      this.visible = false;
      this.currentData = null;
      this.groupsByUnit = new Map();
      this.resizeObserver = null;
      this.frame = 0;
      this.pointerDown = null;
      this.roomHeight = 2.4;
      this.onSelect = typeof options.onSelect === 'function' ? options.onSelect : () => {};
      this.onMove = typeof options.onMove === 'function' ? options.onMove : () => null;
      this.onReady = typeof options.onReady === 'function' ? options.onReady : () => {};
      this.onError = typeof options.onError === 'function' ? options.onError : () => {};
    }

    async init() {
      if (this.ready) return;
      try {
        const { THREE, OrbitControls } = await loadDependencies();
        this.THREE = THREE;
        this.OrbitControls = OrbitControls;
        this.setupScene();
        this.ready = true;
        this.onReady();
        if (this.currentData) this.update(this.currentData);
      } catch (error) {
        console.error('JD Kitchen Builder 3D: failed to load', error);
        this.onError(error);
        throw error;
      }
    }

    setupScene() {
      const THREE = this.THREE;
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xf5f4f1);

      this.camera = new THREE.PerspectiveCamera(43, 1, 0.02, 100);
      this.camera.position.set(4.8, 3.4, 5.4);

      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        powerPreference: 'high-performance'
      });
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.6));
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.05;
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      this.renderer.domElement.className = 'jkb3d__canvas';
      this.renderer.domElement.setAttribute('aria-label', 'Interactive 3D kitchen view');
      this.container.appendChild(this.renderer.domElement);

      this.controls = new this.OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.08;
      // Keep navigation centred on the kitchen. Panning was the main way a
      // touch gesture could leave the room behind and make the model appear lost.
      this.controls.enablePan = false;
      this.controls.screenSpacePanning = false;
      this.controls.minDistance = 1.15;
      this.controls.maxDistance = 12;
      this.controls.minPolarAngle = Math.PI * 0.055;
      this.controls.maxPolarAngle = Math.PI * 0.47;
      this.controls.target.set(0, 0.8, 0);
      this.controls.addEventListener('change', () => this.keepRoomVisible());
      this.controls.update();

      const hemi = new THREE.HemisphereLight(0xffffff, 0xb6afa5, 1.6);
      this.scene.add(hemi);

      const key = new THREE.DirectionalLight(0xffffff, 2.35);
      key.position.set(3.6, 6.5, 4.3);
      key.castShadow = true;
      key.shadow.mapSize.set(1024, 1024);
      key.shadow.camera.near = 0.1;
      key.shadow.camera.far = 18;
      key.shadow.camera.left = -7;
      key.shadow.camera.right = 7;
      key.shadow.camera.top = 7;
      key.shadow.camera.bottom = -7;
      this.scene.add(key);

      const fill = new THREE.DirectionalLight(0xdce7ff, 0.7);
      fill.position.set(-4, 3.5, -2);
      this.scene.add(fill);

      this.modelGroup = new THREE.Group();
      this.scene.add(this.modelGroup);

      this.raycaster = new THREE.Raycaster();
      this.pointer = new THREE.Vector2();
      this.dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

      this.renderer.domElement.addEventListener('pointerdown', e => {
        if (this.startMoveDrag(e)) return;
        this.pointerDown = { x: e.clientX, y: e.clientY };
      });
      this.renderer.domElement.addEventListener('pointermove', e => this.handleMoveDrag(e));
      this.renderer.domElement.addEventListener('pointerup', e => {
        if (this.moveDrag) {
          this.finishMoveDrag(e, false);
          return;
        }
        this.handlePick(e);
      });
      this.renderer.domElement.addEventListener('pointercancel', e => this.finishMoveDrag(e, true));

      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.container);
      this.resize();

      const animate = () => {
        this.frame = requestAnimationFrame(animate);
        if (!this.visible || !this.renderer || !this.scene || !this.camera) return;
        this.controls?.update();
        this.renderer.render(this.scene, this.camera);
      };
      animate();
    }

    show() {
      this.visible = true;
      this.resize();
      if (this.currentData && this.ready) this.update(this.currentData);
    }

    hide() {
      this.visible = false;
    }

    resize() {
      if (!this.renderer || !this.camera) return;
      const width = Math.max(1, this.container.clientWidth);
      const height = Math.max(1, this.container.clientHeight);
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    }

    update(data) {
      const previousRoomWidth = Number(this.currentData?.roomWidth || 0);
      const previousRoomDepth = Number(this.currentData?.roomDepth || 0);
      const nextRoomWidth = Number(data?.roomWidth || 0);
      const nextRoomDepth = Number(data?.roomDepth || 0);
      const roomSizeChanged = previousRoomWidth > 0 &&
        (previousRoomWidth !== nextRoomWidth || previousRoomDepth !== nextRoomDepth);

      this.currentData = data;
      if (!this.ready) return;

      const roomSpan = Math.max(
        Number(data?.roomWidth || 4000),
        Number(data?.roomDepth || 3000)
      ) / 1000;
      // A room-relative zoom limit prevents the camera from disappearing into
      // empty space on small mobile gestures.
      this.controls.maxDistance = Math.max(5.5, roomSpan * 2.35);

      this.clearModel();
      this.roomHeight = this.computeRoomHeight(data);
      this.buildRoom(data);

      const units = Array.isArray(data.units) ? data.units : [];
      const activeLayer = String(data.activeLayer || 'all');
      for (const unit of units) {
        const product = data.productById?.get
          ? data.productById.get(String(unit.productId))
          : (data.catalog || []).find(item => String(item.id) === String(unit.productId));
        if (!product || !product.placeable) continue;
        if (!product.planningKind) {
          const isWall = product.category === 'wall';
          if (activeLayer === 'wall' && !isWall) continue;
          if (activeLayer === 'floor' && isWall) continue;
        }
        const group = this.buildCabinet(unit, product, data);
        if (!group) continue;
        this.modelGroup.add(group);
        this.groupsByUnit.set(String(unit.uid), group);
      }

      this.addAppliedPlinthRuns3D(data);
      this.buildGapRulers(data);
      this.setSelected(data.selectedId || null);
      if (!this.hasFittedView || roomSizeChanged) {
        this.fitView();
        this.hasFittedView = true;
      }
      this.keepRoomVisible();
    }

    gapSegments(data, layer = 'floor') {
      const roomWidth = Math.max(1, Number(data.roomWidth || 4000));
      const roomDepth = Math.max(1, Number(data.roomDepth || 3000));
      const walls = {
        top: { length: roomWidth, intervals: [] },
        bottom: { length: roomWidth, intervals: [] },
        left: { length: roomDepth, intervals: [] },
        right: { length: roomDepth, intervals: [] }
      };
      const wallOnly = layer === 'wall';
      const tolerance = 80;
      const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

      for (const unit of data.units || []) {
        const product = data.productById?.get
          ? data.productById.get(String(unit.productId))
          : (data.catalog || []).find(item => String(item.id) === String(unit.productId));
        if (!product) continue;
        const planningKind = String(product.planningKind || '');
        const planning = Boolean(planningKind);
        const tall = product.category === 'tall';
        if (planning) {
          if (planningKind === 'window' && !wallOnly) continue;
          const featureBottom = Math.max(0, Number(product.elevation) || 0);
          const featureTop = featureBottom + Math.max(0, Number(product.height) || 0);
          const bandBottom = wallOnly ? 1450 : 0;
          const bandTop = wallOnly ? 2300 : 1000;
          if (Math.min(featureTop, bandTop) - Math.max(featureBottom, bandBottom) <= 20) continue;
        }
        if (!planning && !tall && wallOnly !== (product.category === 'wall')) continue;

        const r = this.unitRect(unit, product);
        const touched = [];
        if (r.y <= tolerance) touched.push('top');
        if (roomDepth - (r.y + r.depth) <= tolerance) touched.push('bottom');
        if (r.x <= tolerance) touched.push('left');
        if (roomWidth - (r.x + r.width) <= tolerance) touched.push('right');

        for (const edge of touched) {
          const horizontal = edge === 'top' || edge === 'bottom';
          const intervalStart = horizontal ? r.x : r.y;
          const intervalEnd = intervalStart + (horizontal ? r.width : r.depth);
          walls[edge].intervals.push([
            clamp(intervalStart, 0, walls[edge].length),
            clamp(intervalEnd, 0, walls[edge].length)
          ]);
        }
      }

      const gaps = [];
      for (const [edge, wall] of Object.entries(walls)) {
        const merged = wall.intervals
          .filter(([intervalStart, intervalEnd]) => intervalEnd > intervalStart)
          .sort((left, right) => left[0] - right[0])
          .reduce((result, interval) => {
            const previous = result[result.length - 1];
            if (!previous || interval[0] > previous[1] + 5) result.push([...interval]);
            else previous[1] = Math.max(previous[1], interval[1]);
            return result;
          }, []);

        let cursor = 0;
        for (const [intervalStart, intervalEnd] of merged) {
          if (intervalStart - cursor >= 250) {
            gaps.push({ layer, edge, start: cursor, end: intervalStart, mm: Math.round(intervalStart - cursor) });
          }
          cursor = Math.max(cursor, intervalEnd);
        }
        if (wall.length - cursor >= 250) {
          gaps.push({ layer, edge, start: cursor, end: wall.length, mm: Math.round(wall.length - cursor) });
        }
      }
      return gaps;
    }

    buildGapRulers(data) {
      if (String(data?.roomShape?.type || 'rectangle') !== 'rectangle') return;
      const THREE = this.THREE;
      const roomW = Math.max(1, Number(data.roomWidth || 4000) / 1000);
      const roomD = Math.max(1, Number(data.roomDepth || 3000) / 1000);
      const materials = {
        floor: new THREE.MeshBasicMaterial({ color: 0x182126, depthTest: false }),
        wall: new THREE.MeshBasicMaterial({ color: 0x17689b, depthTest: false })
      };
      const group = new THREE.Group();
      group.userData.kind = 'room';
      group.name = 'clear-space-rulers';

      const lineWeight = 0.010;
      const addBox = (w, d, x, y, z, layer) => {
        const part = new THREE.Mesh(new THREE.BoxGeometry(w, 0.012, d), materials[layer].clone());
        part.position.set(x, y, z);
        part.renderOrder = 9;
        part.userData.kind = 'room';
        group.add(part);
      };

      const activeLayer = String(data.activeLayer || 'all');
      const layers = activeLayer === 'all'
        ? ['floor', 'wall']
        : [activeLayer === 'wall' ? 'wall' : 'floor'];
      const gaps = layers.flatMap(layer => this.gapSegments(data, layer));

      for (const gap of gaps.slice(0, 24)) {
        const length = (gap.end - gap.start) / 1000;
        const mid = (gap.start + gap.end) / 2000;
        if (length < 0.25) continue;

        const isWallRun = gap.layer === 'wall';
        const inset = isWallRun ? 0.16 : 0.27;
        const rulerY = isWallRun ? 1.80 : 0.48;
        const labelY = rulerY + 0.10;
        const tick = 0.075;
        let x = 0;
        let z = 0;

        if (gap.edge === 'top' || gap.edge === 'bottom') {
          x = mid - roomW / 2;
          z = gap.edge === 'top' ? -roomD / 2 + inset : roomD / 2 - inset;
          addBox(Math.max(0.02, length - 0.018), lineWeight, x, rulerY, z, gap.layer);
          addBox(lineWeight, tick, gap.start / 1000 - roomW / 2, rulerY, z, gap.layer);
          addBox(lineWeight, tick, gap.end / 1000 - roomW / 2, rulerY, z, gap.layer);
        } else {
          x = gap.edge === 'left' ? -roomW / 2 + inset : roomW / 2 - inset;
          z = mid - roomD / 2;
          addBox(lineWeight, Math.max(0.02, length - 0.018), x, rulerY, z, gap.layer);
          addBox(tick, lineWeight, x, rulerY, gap.start / 1000 - roomD / 2, gap.layer);
          addBox(tick, lineWeight, x, rulerY, gap.end / 1000 - roomD / 2, gap.layer);
        }

        const foreground = isWallRun ? '#0875b8' : '#111820';
        const label = this.makeRulerLabelSprite(`${gap.mm} mm`, foreground);
        label.position.set(x, labelY, z);
        label.scale.set(0.78, 0.20, 1);
        label.renderOrder = 11;
        label.material.depthTest = false;
        label.userData.kind = 'room';
        group.add(label);
      }

      this.modelGroup.add(group);
    }

    computeRoomHeight(data) {
      let tallest = 2.2;
      for (const unit of data.units || []) {
        const product = data.productById?.get?.(String(unit.productId));
        if (!product) continue;

        if (product.planningKind) {
          const top = ((Number(product.elevation) || 0) + (Number(product.height) || 0)) / 1000;
          tallest = Math.max(tallest, top + 0.25);
          continue;
        }

        const extra = this.hasWorktop(product) ? this.worktopThickness(product) / 1000 : 0;
        tallest = Math.max(tallest, Number(product.height || 0) / 1000 + extra + 0.25);
        if (product.category === 'wall') {
          tallest = Math.max(tallest, 1.45 + Number(product.height || 720) / 1000 + 0.2);
        }
      }
      return Math.max(2.4, Math.min(3.2, tallest));
    }

    clearModel() {
      if (!this.modelGroup) return;
      if (this.selectionHelper) {
        this.scene.remove(this.selectionHelper);
        this.selectionHelper.geometry?.dispose?.();
        this.selectionHelper.material?.dispose?.();
        this.selectionHelper = null;
      }
      if (this.selectionFrontMarker) {
        this.selectionFrontMarker.parent?.remove(this.selectionFrontMarker);
        this.selectionFrontMarker.material?.map?.dispose?.();
        this.selectionFrontMarker.material?.dispose?.();
        this.selectionFrontMarker = null;
      }
      if (this.selectionFrontGuide) {
        this.selectionFrontGuide.parent?.remove(this.selectionFrontGuide);
        this.selectionFrontGuide.geometry?.dispose?.();
        this.selectionFrontGuide.material?.dispose?.();
        this.selectionFrontGuide = null;
      }
      if (this.selectionMoveHandle) {
        this.selectionMoveHandle.parent?.remove(this.selectionMoveHandle);
        this.selectionMoveHandle.material?.map?.dispose?.();
        this.selectionMoveHandle.material?.dispose?.();
        this.selectionMoveHandle = null;
      }
      this.selectedUid = null;
      this.moveDrag = null;
      this.renderer?.domElement?.classList.remove('is-moving-unit');
      if (this.controls) this.controls.enabled = true;
      this.groupsByUnit.clear();
      while (this.modelGroup.children.length) {
        const child = this.modelGroup.children.pop();
        this.disposeObject(child);
      }
    }

    disposeObject(object) {
      const disposeMaterial = material => {
        if (!material) return;
        [
          'map', 'alphaMap', 'aoMap', 'bumpMap', 'displacementMap',
          'emissiveMap', 'envMap', 'lightMap', 'metalnessMap',
          'normalMap', 'roughnessMap'
        ].forEach(key => material[key]?.dispose?.());
        material.dispose?.();
      };

      object?.traverse?.(child => {
        child.geometry?.dispose?.();
        if (Array.isArray(child.material)) child.material.forEach(disposeMaterial);
        else disposeMaterial(child.material);
      });
    }

    makeOakFloorTexture(roomW, roomD) {
      const THREE = this.THREE;
      const canvas = document.createElement('canvas');

      // Render one texture for the complete room. Repeating a smaller canvas
      // caused a visible join through the centre of the floor.
      const pixelsPerMetre = Math.max(
        140,
        Math.min(340, 2048 / Math.max(roomW, roomD))
      );
      canvas.width = Math.max(256, Math.round(roomW * pixelsPerMetre));
      canvas.height = Math.max(256, Math.round(roomD * pixelsPerMetre));
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#c9a36d';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const plankHeight = Math.max(20, Math.round(.15 * pixelsPerMetre));
      const plankWidth = Math.max(150, Math.round(1.2 * pixelsPerMetre));
      const rows = Math.ceil(canvas.height / plankHeight) + 1;
      const tones = [
        [210, 178, 129],
        [202, 165, 112],
        [218, 187, 141],
        [196, 158, 105],
        [207, 171, 119]
      ];

      for (let row = 0; row < rows; row += 1) {
        const y = row * plankHeight;
        const offset = -((row * Math.round(.41 * pixelsPerMetre)) % plankWidth);
        for (let x = offset; x < canvas.width; x += plankWidth) {
          const boardNumber = row + Math.floor((x - offset) / plankWidth);
          const tone = tones[Math.abs(boardNumber) % tones.length];
          const gradient = ctx.createLinearGradient(x, y, x, y + plankHeight);
          gradient.addColorStop(0, `rgb(${tone[0] + 5}, ${tone[1] + 5}, ${tone[2] + 4})`);
          gradient.addColorStop(.48, `rgb(${tone[0]}, ${tone[1]}, ${tone[2]})`);
          gradient.addColorStop(1, `rgb(${tone[0] - 5}, ${tone[1] - 5}, ${tone[2] - 4})`);
          ctx.fillStyle = gradient;
          ctx.fillRect(x + 1, y + 1, plankWidth - 2, plankHeight - 2);

          ctx.strokeStyle = 'rgba(83, 58, 30, .18)';
          ctx.lineWidth = 1.2;
          ctx.strokeRect(x + .6, y + .6, plankWidth - 1.2, plankHeight - 1.2);

          const grainSpacing = plankHeight / 6;
          for (let grain = 1; grain <= 5; grain += 1) {
            const grainY = y + grain * grainSpacing + ((row + grain * 3) % 4);
            ctx.beginPath();
            ctx.strokeStyle = grain % 2
              ? 'rgba(91, 58, 27, .055)'
              : 'rgba(255, 244, 218, .07)';
            ctx.lineWidth = .8;
            ctx.moveTo(x + 12, grainY);
            ctx.bezierCurveTo(
              x + plankWidth * .28, grainY - 3,
              x + plankWidth * .67, grainY + 4,
              x + plankWidth - 12, grainY - 1
            );
            ctx.stroke();
          }
        }
      }

      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.repeat.set(1, 1);
      texture.anisotropy = Math.min(8, this.renderer?.capabilities?.getMaxAnisotropy?.() || 1);
      return texture;
    }

    roomShapePoints(data) {
      const w = Math.max(1000, Number(data?.roomWidth || 4000));
      const d = Math.max(1000, Number(data?.roomDepth || 3000));
      const raw = data?.roomShape && typeof data.roomShape === 'object' ? data.roomShape : {};
      const type = ['rectangle', 'chamfer', 'notch'].includes(String(raw.type || ''))
        ? String(raw.type)
        : 'rectangle';
      const corner = ['top-left', 'top-right', 'bottom-right', 'bottom-left'].includes(String(raw.corner || ''))
        ? String(raw.corner)
        : 'top-right';
      const maxX = Math.max(100, Math.min(3000, Math.round(w * 0.45)));
      const maxY = Math.max(100, Math.min(3000, Math.round(d * 0.45)));
      let x = Math.max(100, Math.min(maxX, Math.round(Number(raw.cutX) || 500)));
      let y = Math.max(100, Math.min(maxY, Math.round(Number(raw.cutY) || 500)));
      if (type === 'chamfer') {
        const size = Math.min(x, y, maxX, maxY);
        x = size;
        y = size;
      }

      if (type === 'rectangle') return [[0, 0], [w, 0], [w, d], [0, d]];

      if (type === 'chamfer') {
        const shapes = {
          'top-left': [[x, 0], [w, 0], [w, d], [0, d], [0, y]],
          'top-right': [[0, 0], [w - x, 0], [w, y], [w, d], [0, d]],
          'bottom-right': [[0, 0], [w, 0], [w, d - y], [w - x, d], [0, d]],
          'bottom-left': [[0, 0], [w, 0], [w, d], [x, d], [0, d - y]]
        };
        return shapes[corner];
      }

      const shapes = {
        'top-left': [[x, 0], [w, 0], [w, d], [0, d], [0, y], [x, y]],
        'top-right': [[0, 0], [w - x, 0], [w - x, y], [w, y], [w, d], [0, d]],
        'bottom-right': [[0, 0], [w, 0], [w, d - y], [w - x, d - y], [w - x, d], [0, d]],
        'bottom-left': [[0, 0], [w, 0], [w, d], [x, d], [x, d - y], [0, d - y]]
      };
      return shapes[corner];
    }

    buildRoom(data) {
      const THREE = this.THREE;
      const roomW = Math.max(1, Number(data.roomWidth || 4000) / 1000);
      const roomD = Math.max(1, Number(data.roomDepth || 3000) / 1000);
      const roomH = this.roomHeight;
      const wallT = 0.045;
      const roomShape = data?.roomShape && typeof data.roomShape === 'object'
        ? data.roomShape
        : { type: 'rectangle' };

      const floorMat = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        map: this.makeOakFloorTexture(roomW, roomD),
        roughness: 0.82,
        metalness: 0,
        side: THREE.DoubleSide
      });

      const wallColours = {
        'white': 0xf6f5f2,
        'soft-grey': 0xd9dbdc,
        'warm-grey': 0xc8c2b9,
        'greige': 0xcfc7bb,
        'sage': 0xb7c0ad
      };
      const wallMat = new THREE.MeshStandardMaterial({
        color: wallColours[data.wallColour] || wallColours.white,
        roughness: 0.92,
        metalness: 0
      });

      // Keep the original fast rectangular room path unchanged for existing
      // projects. Custom geometry is only used after the room-shape tool is used.
      if (String(roomShape.type || 'rectangle') === 'rectangle') {
        const floor = new THREE.Mesh(new THREE.BoxGeometry(roomW, 0.04, roomD), floorMat);
        floor.position.y = -0.02;
        floor.receiveShadow = true;
        floor.userData.kind = 'room';
        this.modelGroup.add(floor);

        const back = new THREE.Mesh(new THREE.BoxGeometry(roomW, roomH, wallT), wallMat);
        back.position.set(0, roomH / 2, roomD / 2 + wallT / 2);
        back.receiveShadow = true;
        back.userData.kind = 'room';
        back.userData.wallEdge = 'back';
        this.modelGroup.add(back);

        const left = new THREE.Mesh(new THREE.BoxGeometry(wallT, roomH, roomD), wallMat.clone());
        left.position.set(-roomW / 2 - wallT / 2, roomH / 2, 0);
        left.receiveShadow = true;
        left.userData.kind = 'room';
        left.userData.wallEdge = 'left';
        this.modelGroup.add(left);

        const right = new THREE.Mesh(new THREE.BoxGeometry(wallT, roomH, roomD), wallMat.clone());
        right.position.set(roomW / 2 + wallT / 2, roomH / 2, 0);
        right.receiveShadow = true;
        right.userData.kind = 'room';
        right.userData.wallEdge = 'right';
        this.modelGroup.add(right);
        return;
      }

      const pointsMm = this.roomShapePoints(data);
      const points = pointsMm.map(([px, py]) => ({
        x: px / 1000 - roomW / 2,
        z: py / 1000 - roomD / 2,
        px,
        py
      }));

      // ShapeGeometry is created in XY then rotated into the XZ floor plane.
      // Reverse the planner polygon so the resulting top face has an upward normal.
      const floorPoints = [...points].reverse();
      const shape = new THREE.Shape();
      floorPoints.forEach((point, index) => {
        const shapeY = -point.z;
        if (index === 0) shape.moveTo(point.x, shapeY);
        else shape.lineTo(point.x, shapeY);
      });
      shape.closePath();

      const floorGeometry = new THREE.ShapeGeometry(shape);
      floorGeometry.rotateX(-Math.PI / 2);
      const floor = new THREE.Mesh(floorGeometry, floorMat);
      floor.position.y = 0;
      floor.receiveShadow = true;
      floor.userData.kind = 'room';
      floor.userData.roomShape = String(roomShape.type || '');
      this.modelGroup.add(floor);

      // The current rectangular room deliberately leaves planner y=0 open so
      // the camera can look into the kitchen. Preserve that rule, while drawing
      // every new angled/recess wall created by the edited corner.
      for (let index = 0; index < points.length; index += 1) {
        const a = points[index];
        const b = points[(index + 1) % points.length];
        const isOpenFront = Math.abs(a.py) < 1 && Math.abs(b.py) < 1;
        if (isOpenFront) continue;

        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const length = Math.hypot(dx, dz);
        if (length < 0.02) continue;

        const wall = new THREE.Mesh(
          new THREE.BoxGeometry(length, roomH, wallT),
          wallMat.clone()
        );
        wall.position.set(
          (a.x + b.x) / 2,
          roomH / 2,
          (a.z + b.z) / 2
        );
        wall.rotation.y = -Math.atan2(dz, dx);
        wall.receiveShadow = true;
        wall.userData.kind = 'room';
        wall.userData.wallEdge = 'custom';
        wall.userData.roomShapeSegment = index;
        this.modelGroup.add(wall);
      }
    }

    buildCabinet(unit, product, data) {
      const THREE = this.THREE;

      if (product.planningKind) {
        return this.buildPlanningFeature(unit, product, data);
      }

      if (product.cornerShape || product.category === 'corner') {
        return this.buildCornerCabinet(unit, product, data);
      }

      const group = new THREE.Group();
      group.userData.unitId = String(unit.uid);
      group.userData.kind = 'cabinet';

      const width = Number(product.width || 500) / 1000;
      const depth = Number(product.depth || 500) / 1000;
      const fittedDepth = this.fittedRunDepth(product) / 1000 || depth;
      const serviceGap = this.serviceGapForUnit(unit, product) / 1000;
      const bodyOffsetZ = this.isFittedBaseRun(product)
        ? (-fittedDepth / 2 + serviceGap + depth / 2)
        : 0;
      const totalHeight = Number(product.height || 820) / 1000;
      const adjustable = this.isAdjustable(product);
      const utilitySink = this.isUtilitySink(product);
      const wall = product.category === 'wall';
      const plinthHeight = wall ? 0 : this.plinthHeight(product);
      const legHeight = adjustable && !wall ? 0.15 : 0;
      const bodyHeight = Math.max(0.18, totalHeight - plinthHeight);
      const bodyBottom = wall ? this.wallBottom(product) : plinthHeight;
      const bodyCenterY = bodyBottom + bodyHeight / 2;

      // Local +Z is always the cabinet front. Keep this metadata so the
      // selected-cabinet helper can mark the front even after room rotation.
      group.userData.frontDepth = bodyOffsetZ + depth / 2 + 0.13;
      group.userData.frontWidth = width;
      group.userData.frontY = wall
        ? bodyBottom + Math.min(0.13, bodyHeight * 0.18)
        : Math.max(0.07, plinthHeight + 0.04);

      const carcassMat = new THREE.MeshStandardMaterial({
        color: 0xf4f3ef,
        roughness: 0.72,
        metalness: 0
      });
      const frontMat = this.frontMaterial(product);
      const edgeMat = new THREE.MeshStandardMaterial({
        color: 0xf1f0ec,
        roughness: 0.72,
        metalness: 0
      });
      const darkMat = new THREE.MeshStandardMaterial({
        color: 0x1d2023,
        roughness: 0.5,
        metalness: 0.05
      });
      const metalMat = new THREE.MeshPhysicalMaterial({
        color: 0xe2e5e7,
        roughness: 0.24,
        metalness: 0.42,
        clearcoat: 0.38,
        clearcoatRoughness: 0.16
      });

      const bodyGroup = new THREE.Group();
      bodyGroup.position.z = bodyOffsetZ;
      bodyGroup.userData.unitId = String(unit.uid);
      group.add(bodyGroup);

      if (this.isFittedBaseRun(product) && serviceGap > 0) {
        this.addServiceGapGuide(
          group,
          width,
          fittedDepth,
          serviceGap,
          unit.uid,
          String(data?.selectedId || '') === String(unit.uid)
        );
      }

      this.addCarcassInterior(
        bodyGroup,
        width,
        bodyHeight,
        depth,
        bodyBottom,
        edgeMat,
        unit.uid,
        product
      );

      const sideThickness = 0.016;
      const freestandingSides = !wall && !adjustable;
      const sideHeight = freestandingSides ? totalHeight : Math.max(0.1, bodyHeight - 0.004);
      const sideCentreY = freestandingSides ? totalHeight / 2 : bodyCenterY;
      const sideLeft = new THREE.Mesh(
        new THREE.BoxGeometry(sideThickness, sideHeight, depth + 0.004),
        edgeMat.clone()
      );
      sideLeft.position.set(-width / 2 + sideThickness / 2, sideCentreY, 0);
      sideLeft.castShadow = true;
      sideLeft.receiveShadow = true;
      sideLeft.userData.unitId = String(unit.uid);
      bodyGroup.add(sideLeft);

      const sideRight = sideLeft.clone();
      sideRight.material = edgeMat.clone();
      sideRight.position.x = width / 2 - sideThickness / 2;
      sideRight.userData.unitId = String(unit.uid);
      bodyGroup.add(sideRight);

      this.addAppliedEndPanels(
        group,
        unit,
        product,
        data,
        width,
        fittedDepth
      );

      if (adjustable && !wall) {
        this.addAdjustableLegs(bodyGroup, width, depth, legHeight, darkMat, unit.uid);
      }

      if (!wall && plinthHeight > 0) {
        const colourMatchedPlinth = adjustable && Array.isArray(data?.plinthRuns) &&
          data.plinthRuns.some(item =>
            Array.isArray(item.unitIds) &&
            item.unitIds.some(id => String(id) === String(unit.uid))
          );
        this.addPlinth(bodyGroup, width, depth, plinthHeight, frontMat, unit.uid, {
          adjustable,
          utilitySink,
          colourMatched: colourMatchedPlinth
        });

        // Continuous colour-matched runs are rendered once at room level
        // after all cabinets are built. This avoids one strip per cabinet.
      }

      if (product.category === 'drawer') {
        this.addDrawers(bodyGroup, width, bodyHeight, depth, bodyBottom, frontMat, metalMat, unit.uid);
      } else if (product.category === 'oven') {
        this.addOvenFront(bodyGroup, width, bodyHeight, depth, bodyBottom, frontMat, darkMat, metalMat, unit.uid);
      } else if (product.category === 'tall') {
        this.addTallFront(bodyGroup, width, bodyHeight, depth, bodyBottom, frontMat, metalMat, unit.uid, product, unit);
      } else {
        this.addDoors(bodyGroup, width, bodyHeight, depth, bodyBottom, frontMat, metalMat, unit.uid, product, unit);

        if (this.isBridgingUnit(product)) {
          this.addExtractorHood(bodyGroup, width, depth, bodyBottom, metalMat, darkMat, unit.uid);
        }
      }

      if (this.hasWorktop(product) || utilitySink) {
        this.addWorktop(group, width, depth, totalHeight, product, unit.uid);
      }

      if (utilitySink) {
        this.addSinkAndTap(bodyGroup, width, totalHeight, product, unit.uid);
      }

      if (product.category === 'corner') {
        const cornerTag = this.makeLabelSprite('CORNER', '#1f2327', '#ffffff');
        cornerTag.position.set(0, (wall ? bodyBottom + bodyHeight : totalHeight) + 0.11, depth * 0.2);
        cornerTag.userData.unitId = String(unit.uid);
        group.add(cornerTag);
      }

      const isNew = data.justAddedIds?.has
        ? data.justAddedIds.has(unit.uid)
        : Array.isArray(data.justAddedIds) && data.justAddedIds.includes(unit.uid);
      if (isNew) {
        const newTag = this.makeLabelSprite('NEW', '#111111', '#ffffff');
        const topY = wall
          ? bodyBottom + bodyHeight + 0.10
          : totalHeight + (this.hasWorktop(product) || utilitySink ? this.worktopThickness(product) / 1000 : 0) + 0.10;
        newTag.position.set(0, topY, 0);
        newTag.scale.set(0.28, 0.09, 1);
        newTag.userData.unitId = String(unit.uid);
        group.add(newTag);
      }

      const rect = this.unitRect(unit, product);
      const roomW = Number(data.roomWidth || 4000);
      const roomD = Number(data.roomDepth || 3000);
      const cx = rect.x + rect.width / 2;
      const cz = rect.y + rect.depth / 2;
      group.position.x = (cx - roomW / 2) / 1000;
      group.position.z = (cz - roomD / 2) / 1000;

      const forcedFront = String(unit.frontEdgeOverride || '');
      if (unit.wallLinked === false && ['top', 'right', 'bottom', 'left'].includes(forcedFront)) {
        const backForFront = { top: 'bottom', right: 'left', bottom: 'top', left: 'right' };
        group.rotation.y = this.rotationForEdge(backForFront[forcedFront]);
      } else {
        const edge = this.nearestEdge(rect, roomW, roomD);
        const expectedRotation = (edge === 'left' || edge === 'right') ? 90 : 0;
        const actualRotation = Number(unit.rotation) === 90 ? 90 : 0;
        const extraRotation = (actualRotation - expectedRotation) * Math.PI / 180;
        group.rotation.y = this.rotationForEdge(edge) + extraRotation;
      }

      group.traverse(child => {
        if (!child.userData.unitId && child.userData.kind !== 'room') {
          child.userData.unitId = String(unit.uid);
        }
      });

      return group;
    }

    addServiceGapGuide(group, width, fittedDepth, serviceGap, uid, selected = false) {
      const THREE = this.THREE;
      if (!(serviceGap > 0)) return;

      const guide = new THREE.Mesh(
        new THREE.BoxGeometry(
          Math.max(0.05, width - 0.02),
          0.008,
          Math.max(0.01, serviceGap)
        ),
        new THREE.MeshBasicMaterial({
          color: 0x5f9fbe,
          transparent: true,
          opacity: selected ? 0.30 : 0.14,
          depthWrite: false
        })
      );
      guide.position.set(
        0,
        0.006,
        -fittedDepth / 2 + serviceGap / 2
      );
      guide.userData.unitId = String(uid);
      guide.userData.serviceGapGuide = true;
      group.add(guide);

      if (selected) {
        const label = this.makeLabelSprite(
          `${Math.round(serviceGap * 1000)} mm GAP`,
          '#eef8fc',
          '#245e7e'
        );
        label.position.set(
          0,
          0.055,
          -fittedDepth / 2 + serviceGap / 2
        );
        label.scale.set(0.30, 0.075, 1);
        label.userData.unitId = String(uid);
        label.userData.serviceGapGuide = true;
        group.add(label);
      }
    }

    addAppliedEndPanels(group, unit, product, data, cabinetWidth, cabinetDepth) {
      const THREE = this.THREE;
      const panels = Array.isArray(data?.endPanels)
        ? data.endPanels.filter(panel => String(panel.unitId) === String(unit.uid))
        : [];
      if (!panels.length) return;

      const wall = product.category === 'wall';

      for (const panel of panels) {
        const panelColour = this.colourHex(panel.colour || 'White');
        const panelGloss = /gloss/i.test(String(panel.colour || ''));
        const panelMaterial = new THREE.MeshPhysicalMaterial({
          color: panelColour,
          roughness: panelGloss ? 0.23 : 0.58,
          metalness: 0,
          clearcoat: panelGloss ? 0.62 : 0.10,
          clearcoatRoughness: panelGloss ? 0.18 : 0.62
        });
        const thickness = Math.max(0.012, Number(panel.thickness || 16) / 1000);
        const height = Math.max(0.10, Number(panel.height || product.height || 820) / 1000);
        const depth = Math.max(0.10, Number(panel.depth || product.depth || 500) / 1000);

        // End panels sit outside the carcass side. Their rear edge aligns with
        // the cabinet rear, so deeper base panels project forwards as they do
        // on the real product.
        const x = panel.localSide === 'right'
          ? cabinetWidth / 2 + thickness / 2
          : -cabinetWidth / 2 - thickness / 2;
        const z = -cabinetDepth / 2 + depth / 2;
        const bottom = wall ? this.wallBottom(product) : 0;
        const y = bottom + height / 2;

        const mesh = new THREE.Mesh(
          new THREE.BoxGeometry(thickness, height, depth),
          panelMaterial
        );
        mesh.position.set(x, y, z);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData.unitId = String(unit.uid);
        mesh.userData.endPanelSku = String(panel.sku || '');
        mesh.userData.endPanelSide = String(panel.localSide || '');
        group.add(mesh);

        // All visible faces, including the thin front edge, use the actual
        // selected panel finish. Do not overlay a grey/white edge strip.

      }
    }


    buildCornerCabinet(unit, product, data) {
      const THREE = this.THREE;
      const group = new THREE.Group();
      group.userData.unitId = String(unit.uid);
      group.userData.kind = 'cabinet';
      group.userData.cornerCabinet = true;

      const width = Math.max(0.6, Number(product.width || 900) / 1000);
      const depth = Math.max(0.6, Number(product.depth || product.width || 900) / 1000);
      const totalHeight = Number(product.height || 870) / 1000;
      const wall = product.category === 'wall';
      const adjustable = this.isAdjustable(product);
      const plinthHeight = wall ? 0 : this.plinthHeight(product);
      const bodyBottom = wall ? this.wallBottom(product) : plinthHeight;
      const bodyHeight = wall ? totalHeight : Math.max(0.5, totalHeight - plinthHeight);
      const bodyTop = bodyBottom + bodyHeight;
      const panel = 0.016;
      const requestedArm = Number(product.cornerArmDepth || (adjustable ? 578 : 463)) / 1000;
      const armDepth = Math.min(width - 0.18, depth - 0.18, Math.max(0.36, requestedArm));
      const innerX = -width / 2 + armDepth;
      const innerZ = -depth / 2 + armDepth;
      const doorSpanX = Math.max(0.18, width - armDepth - 0.008);
      const doorSpanZ = Math.max(0.18, depth - armDepth - 0.008);
      const centreY = bodyBottom + bodyHeight / 2;

      const edgeMat = new THREE.MeshStandardMaterial({
        color: 0xf1f0ec,
        roughness: 0.72,
        metalness: 0
      });
      const insideMat = new THREE.MeshStandardMaterial({
        color: 0xf8f7f3,
        roughness: 0.76,
        metalness: 0
      });
      const frontMat = this.frontMaterial(product);
      const metalMat = new THREE.MeshPhysicalMaterial({
        color: 0xe2e5e7,
        roughness: 0.24,
        metalness: 0.42,
        clearcoat: 0.38,
        clearcoatRoughness: 0.16
      });
      const darkMat = new THREE.MeshStandardMaterial({
        color: 0x1d2023,
        roughness: 0.5,
        metalness: 0.05
      });

      const addBox = (w, h, d, x, y, z, material = edgeMat) => {
        const mesh = new THREE.Mesh(
          new THREE.BoxGeometry(Math.max(0.012, w), Math.max(0.012, h), Math.max(0.012, d)),
          material.clone()
        );
        mesh.position.set(x, y, z);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData.unitId = String(unit.uid);
        group.add(mesh);
        return mesh;
      };

      // L-shaped bottom and shelf: full rear arm plus a perpendicular left arm.
      const rearArmCentreZ = -depth / 2 + armDepth / 2;
      const leftArmCentreX = -width / 2 + armDepth / 2;
      addBox(width - panel * 2, panel, armDepth - panel, 0, bodyBottom + panel / 2, rearArmCentreZ, insideMat);
      addBox(armDepth - panel, panel, depth - armDepth, leftArmCentreX, bodyBottom + panel / 2, innerZ + (depth - armDepth) / 2, insideMat);

      const shelfY = bodyBottom + bodyHeight * 0.52;
      addBox(width - panel * 2, panel, armDepth - panel * 2, 0, shelfY, rearArmCentreZ, insideMat);
      addBox(armDepth - panel * 2, panel, depth - armDepth, leftArmCentreX, shelfY, innerZ + (depth - armDepth) / 2, insideMat);

      // Outer carcass panels follow the two room walls; short end panels close each arm.
      addBox(width, bodyHeight, panel, 0, centreY, -depth / 2 + panel / 2);
      addBox(panel, bodyHeight, depth, -width / 2 + panel / 2, centreY, 0);
      addBox(panel, bodyHeight, armDepth, width / 2 - panel / 2, centreY, rearArmCentreZ);
      addBox(armDepth, bodyHeight, panel, leftArmCentreX, centreY, depth / 2 - panel / 2);

      // WCORNER wall units have a single solid pentagonal top panel, matching
      // the real cabinet. Floor corner units retain their existing construction:
      // ALBC900 uses the open rail frame; fixed BC units use the L-shaped top.
      if (wall) {
        const shape = new THREE.Shape();
        const xLeft = -width / 2 + panel;
        const xRight = width / 2 - panel;
        const zBack = -depth / 2 + panel;
        const zFront = depth / 2 - panel;
        const zRightFront = innerZ;
        const xFrontRight = innerX;

        // ShapeGeometry uses XY; use -Z as Y so after rotating onto the XZ
        // plane the visible footprint follows the carcass and diagonal door.
        const points = [
          [xLeft, -zBack],
          [xRight, -zBack],
          [xRight, -zRightFront],
          [xFrontRight, -zFront],
          [xLeft, -zFront]
        ];
        points.forEach(([x, y], index) => {
          if (index === 0) shape.moveTo(x, y);
          else shape.lineTo(x, y);
        });
        shape.closePath();

        const geometry = new THREE.ExtrudeGeometry(shape, {
          depth: panel,
          bevelEnabled: false,
          steps: 1
        });
        geometry.rotateX(-Math.PI / 2);

        const top = new THREE.Mesh(geometry, insideMat.clone());
        top.position.y = bodyTop - panel;
        top.castShadow = true;
        top.receiveShadow = true;
        top.userData.unitId = String(unit.uid);
        top.userData.solidCornerTop = true;
        group.add(top);
      } else if (adjustable) {
        const rail = 0.060;
        addBox(width - panel * 2, panel, rail, 0, bodyTop - panel / 2, -depth / 2 + panel + rail / 2);
        addBox(rail, panel, depth - panel * 2, -width / 2 + panel + rail / 2, bodyTop - panel / 2, 0);
        addBox(doorSpanX, panel, rail, innerX + doorSpanX / 2, bodyTop - panel / 2, innerZ - rail / 2);
        addBox(rail, panel, doorSpanZ, innerX - rail / 2, bodyTop - panel / 2, innerZ + doorSpanZ / 2);
      } else {
        addBox(width - panel * 2, panel, armDepth - panel, 0, bodyTop - panel / 2, rearArmCentreZ);
        addBox(armDepth - panel, panel, depth - armDepth, leftArmCentreX, bodyTop - panel / 2, innerZ + (depth - armDepth) / 2);
      }

      if (adjustable) {
        const legHeight = 0.15;
        const legGeo = new THREE.CylinderGeometry(0.027, 0.029, legHeight, 12);
        const inset = 0.075;
        const legPoints = [
          [-width / 2 + inset, -depth / 2 + inset],
          [width / 2 - inset, -depth / 2 + inset],
          [-width / 2 + inset, depth / 2 - inset],
          [width / 2 - inset, innerZ - inset],
          [innerX - inset, depth / 2 - inset]
        ];
        legPoints.forEach(([x, z]) => {
          const leg = new THREE.Mesh(legGeo, darkMat.clone());
          leg.position.set(x, legHeight / 2, z);
          leg.castShadow = true;
          leg.userData.unitId = String(unit.uid);
          group.add(leg);
        });
      }

      if (plinthHeight > 0) {
        const colourMatchedPlinth = adjustable && Array.isArray(data?.plinthRuns) &&
          data.plinthRuns.some(item =>
            Array.isArray(item.unitIds) &&
            item.unitIds.some(id => String(id) === String(unit.uid))
          );
        const plinthMat = colourMatchedPlinth
          ? frontMat.clone()
          : new THREE.MeshStandardMaterial({
              color: 0xf7f7f4,
              roughness: 0.68,
              metalness: 0
            });
        addBox(doorSpanX, Math.max(0.04, plinthHeight - 0.006), panel, innerX + doorSpanX / 2, plinthHeight / 2, innerZ - 0.042, plinthMat);
        addBox(panel, Math.max(0.04, plinthHeight - 0.006), doorSpanZ, innerX - 0.042, plinthHeight / 2, innerZ + doorSpanZ / 2, plinthMat);

        // Continuous colour-matched runs are rendered once at room level.
      }

      const doorHeight = Math.max(0.45, bodyHeight - 0.008);
      const doorY = bodyBottom + bodyHeight / 2;
      const handleLength = Math.min(0.16, Math.max(0.105, doorHeight * 0.20));
      const addHandle = (x, z) => {
        const handle = new THREE.Mesh(
          new THREE.CylinderGeometry(0.0065, 0.0065, handleLength, 14),
          metalMat.clone()
        );
        handle.position.set(x, doorY + 0.02, z);
        handle.castShadow = true;
        handle.userData.unitId = String(unit.uid);
        group.add(handle);
      };

      if (wall) {
        // WCORNER uses one diagonal door across the inside of its 600 x 600
        // L-shaped carcass. The 313 mm arms leave the correct door opening.
        const diagonalWidth = Math.sqrt(doorSpanX * doorSpanX + doorSpanZ * doorSpanZ);
        const diagonalDoor = addBox(
          diagonalWidth,
          doorHeight,
          0.020,
          innerX + doorSpanX / 2,
          doorY,
          innerZ + doorSpanZ / 2,
          frontMat
        );
        diagonalDoor.rotation.y = Math.atan2(doorSpanZ, doorSpanX);
        diagonalDoor.userData.cornerFront = true;
        addHandle(
          innerX + doorSpanX * 0.64 + 0.018,
          innerZ + doorSpanZ * 0.36 + 0.018
        );
      } else {
        // Base corners use two real perpendicular fronts meeting at the inside corner.
        const horizontalDoor = addBox(doorSpanX, doorHeight, 0.020, innerX + doorSpanX / 2, doorY, innerZ + 0.011, frontMat);
        horizontalDoor.userData.cornerFront = true;
        const sideDoor = addBox(0.020, doorHeight, doorSpanZ, innerX + 0.011, doorY, innerZ + doorSpanZ / 2, frontMat);
        sideDoor.userData.cornerFront = true;
        addHandle(innerX + 0.050, innerZ + 0.031);
        addHandle(innerX + 0.031, innerZ + 0.050);
      }

      if (this.hasWorktop(product)) {
        const topThickness = this.worktopThickness(product) / 1000;
        const topY = totalHeight + topThickness / 2;
        const worktopMat = this.worktopMaterial(product);
        addBox(width + 0.025, topThickness, armDepth + 0.025, 0, topY, rearArmCentreZ, worktopMat);
        addBox(armDepth + 0.025, topThickness, depth - armDepth, leftArmCentreX, topY, innerZ + (depth - armDepth) / 2, worktopMat);
      }

      const cornerTag = this.makeLabelSprite(wall ? 'WALL CORNER' : 'L-CORNER', '#1f2327', '#ffffff');
      cornerTag.position.set(-width * 0.20, bodyBottom + bodyHeight + 0.11, -depth * 0.20);
      cornerTag.userData.unitId = String(unit.uid);
      group.add(cornerTag);

      const isNew = data.justAddedIds?.has
        ? data.justAddedIds.has(unit.uid)
        : Array.isArray(data.justAddedIds) && data.justAddedIds.includes(unit.uid);
      if (isNew) {
        const newTag = this.makeLabelSprite('NEW', '#111111', '#ffffff');
        newTag.position.set(-width * 0.20, bodyBottom + bodyHeight + 0.21, -depth * 0.20);
        newTag.scale.set(0.28, 0.09, 1);
        newTag.userData.unitId = String(unit.uid);
        group.add(newTag);
      }

      const rect = this.unitRect(unit, product);
      const roomW = Number(data.roomWidth || 4000);
      const roomD = Number(data.roomDepth || 3000);
      group.position.x = (rect.x + rect.width / 2 - roomW / 2) / 1000;
      group.position.z = (rect.y + rect.depth / 2 - roomD / 2) / 1000;

      const corner = this.nearestCorner(rect, roomW, roomD);
      const rotations = {
        'top-left': 0,
        'top-right': -Math.PI / 2,
        'bottom-right': Math.PI,
        'bottom-left': Math.PI / 2
      };
      const cornerTurn = ((Number(unit.cornerTurn) || 0) % 4 + 4) % 4;
      group.rotation.y = (rotations[corner] || 0) + cornerTurn * Math.PI / 2;
      group.userData.roomCorner = corner;
      group.userData.cornerTurn = cornerTurn;

      group.traverse(child => {
        if (!child.userData.unitId && child.userData.kind !== 'room') {
          child.userData.unitId = String(unit.uid);
        }
      });

      return group;
    }

    buildPlanningFeature(unit, product, data) {
      const THREE = this.THREE;
      const kind = String(product.planningKind || 'obstacle');
      const group = new THREE.Group();
      group.userData.unitId = String(unit.uid);
      group.userData.kind = 'cabinet';

      const width = Math.max(0.1, Number(product.width || 600) / 1000);
      const depth = Math.max(0.05, Number(product.depth || 300) / 1000);
      const height = Math.max(0.1, Number(product.height || 1000) / 1000);
      const elevation = Math.max(0, Number(product.elevation || 0) / 1000);

      const palette = {
        door: 0xd5a15d,
        window: 0x8fb7ce,
        radiator: 0xb9bec3,
        fridge: 0xd9dde0,
        obstacle: 0xd68181
      };

      const fill = new THREE.MeshStandardMaterial({
        color: palette[kind] || palette.obstacle,
        roughness: 0.62,
        metalness: kind === 'radiator' ? 0.18 : 0,
        transparent: true,
        opacity: kind === 'obstacle' ? 0.38 : 0.62,
        side: THREE.DoubleSide
      });

      const lineMat = new THREE.MeshStandardMaterial({
        color: 0x44494e,
        roughness: 0.55,
        metalness: kind === 'radiator' ? 0.2 : 0
      });

      if (kind === 'door') {
        const clearZone = new THREE.Mesh(
          new THREE.BoxGeometry(width, 0.018, depth),
          fill.clone()
        );
        clearZone.position.set(0, 0.01, 0);
        clearZone.receiveShadow = true;
        group.add(clearZone);

        const frameT = 0.035;
        const frameDepth = 0.04;
        const top = new THREE.Mesh(new THREE.BoxGeometry(width, frameT, frameDepth), lineMat.clone());
        top.position.set(0, height, -depth / 2 + frameDepth / 2);
        group.add(top);

        const sideA = new THREE.Mesh(new THREE.BoxGeometry(frameT, height, frameDepth), lineMat.clone());
        sideA.position.set(-width / 2 + frameT / 2, height / 2, -depth / 2 + frameDepth / 2);
        group.add(sideA);

        const sideB = sideA.clone();
        sideB.material = lineMat.clone();
        sideB.position.x = width / 2 - frameT / 2;
        group.add(sideB);
      } else if (kind === 'window') {
        const sill = elevation;
        const panel = new THREE.Mesh(
          new THREE.BoxGeometry(width, height, 0.035),
          new THREE.MeshPhysicalMaterial({
            color: 0xb9d9e8,
            roughness: 0.12,
            metalness: 0,
            transmission: 0.16,
            transparent: true,
            opacity: 0.56,
            clearcoat: 0.3
          })
        );
        panel.position.set(0, sill + height / 2, -depth / 2 + 0.025);
        group.add(panel);

        const frameT = 0.035;
        const frameDepth = 0.05;
        const top = new THREE.Mesh(new THREE.BoxGeometry(width, frameT, frameDepth), lineMat.clone());
        top.position.set(0, sill + height, -depth / 2 + frameDepth / 2);
        group.add(top);
        const bottom = top.clone();
        bottom.material = lineMat.clone();
        bottom.position.y = sill;
        group.add(bottom);
        const side = new THREE.Mesh(new THREE.BoxGeometry(frameT, height, frameDepth), lineMat.clone());
        side.position.set(-width / 2 + frameT / 2, sill + height / 2, -depth / 2 + frameDepth / 2);
        group.add(side);
        const side2 = side.clone();
        side2.material = lineMat.clone();
        side2.position.x = width / 2 - frameT / 2;
        group.add(side2);
      } else if (kind === 'fridge') {
        const applianceMat = new THREE.MeshPhysicalMaterial({
          color: 0xd9dde0,
          roughness: 0.28,
          metalness: 0.34,
          clearcoat: 0.25,
          clearcoatRoughness: 0.2
        });
        const dark = new THREE.MeshStandardMaterial({
          color: 0x3d4348,
          roughness: 0.48,
          metalness: 0.2
        });
        const body = new THREE.Mesh(
          new THREE.BoxGeometry(width, height, depth),
          applianceMat
        );
        body.position.set(0, elevation + height / 2, 0);
        body.castShadow = true;
        body.receiveShadow = true;
        group.add(body);

        const frontZ = depth / 2 + 0.006;
        const split = new THREE.Mesh(
          new THREE.BoxGeometry(Math.max(0.08, width - 0.03), 0.012, 0.012),
          dark.clone()
        );
        split.position.set(0, elevation + height * 0.34, frontZ);
        group.add(split);

        const handleTop = new THREE.Mesh(
          new THREE.BoxGeometry(0.015, Math.max(0.18, height * 0.22), 0.018),
          dark.clone()
        );
        handleTop.position.set(width * 0.32, elevation + height * 0.64, frontZ + 0.008);
        group.add(handleTop);

        const handleBottom = handleTop.clone();
        handleBottom.material = dark.clone();
        handleBottom.scale.y = 0.62;
        handleBottom.position.y = elevation + height * 0.21;
        group.add(handleBottom);
      } else if (kind === 'dishwasher') {
        const steel = new THREE.MeshStandardMaterial({ color: palette.dishwasher.body, roughness: 0.42, metalness: 0.55 });
        const trim = new THREE.MeshStandardMaterial({ color: palette.dishwasher.accent, roughness: 0.3, metalness: 0.7 });
        const body = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), steel);
        body.position.y = elevation + height / 2;
        group.add(body);
        const panel = new THREE.Mesh(new THREE.BoxGeometry(width * 0.9, Math.min(0.11, height * 0.14), 0.025), trim);
        panel.position.set(0, elevation + height * 0.88, depth / 2 + 0.014);
        group.add(panel);
        const handle = new THREE.Mesh(new THREE.BoxGeometry(width * 0.58, 0.025, 0.035), trim);
        handle.position.set(0, elevation + height * 0.79, depth / 2 + 0.025);
        group.add(handle);
      } else if (kind === 'oven-appliance') {
        const dark = new THREE.MeshStandardMaterial({ color: palette['oven-appliance'].body, roughness: 0.3, metalness: 0.25 });
        const steel = new THREE.MeshStandardMaterial({ color: palette['oven-appliance'].accent, roughness: 0.32, metalness: 0.72 });
        const glass = new THREE.MeshPhysicalMaterial({ color: '#111820', roughness: 0.12, metalness: 0.15, transparent: true, opacity: 0.86 });
        const body = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), dark);
        body.position.y = elevation + height / 2;
        group.add(body);
        const fascia = new THREE.Mesh(new THREE.BoxGeometry(width * 0.92, height * 0.16, 0.035), steel);
        fascia.position.set(0, elevation + height * 0.82, depth / 2 + 0.02);
        group.add(fascia);
        const door = new THREE.Mesh(new THREE.BoxGeometry(width * 0.88, height * 0.48, 0.04), glass);
        door.position.set(0, elevation + height * 0.46, depth / 2 + 0.023);
        group.add(door);
        const handle = new THREE.Mesh(new THREE.BoxGeometry(width * 0.68, 0.025, 0.035), steel);
        handle.position.set(0, elevation + height * 0.69, depth / 2 + 0.04);
        group.add(handle);
        const hob = new THREE.Mesh(new THREE.BoxGeometry(width * 0.94, 0.025, depth * 0.86), glass);
        hob.position.set(0, elevation + height + 0.014, 0);
        group.add(hob);
      } else if (kind === 'hood') {
        const steel = new THREE.MeshStandardMaterial({ color: palette.hood.body, roughness: 0.34, metalness: 0.72 });
        const dark = new THREE.MeshStandardMaterial({ color: palette.hood.accent, roughness: 0.48, metalness: 0.35 });
        const canopyH = Math.min(0.16, Math.max(0.09, height * 0.22));
        const canopy = new THREE.Mesh(new THREE.BoxGeometry(width, canopyH, depth), steel);
        canopy.position.set(0, elevation + canopyH / 2, 0);
        group.add(canopy);
        const chimneyH = Math.max(0.18, height - canopyH);
        const chimney = new THREE.Mesh(new THREE.BoxGeometry(width * 0.34, chimneyH, Math.min(depth * 0.5, 0.24)), steel);
        chimney.position.set(0, elevation + canopyH + chimneyH / 2, -depth * 0.2);
        group.add(chimney);
        const filter = new THREE.Mesh(new THREE.BoxGeometry(width * 0.75, 0.022, depth * 0.62), dark);
        filter.position.set(0, elevation - 0.012, depth * 0.05);
        group.add(filter);
      } else if (kind === 'radiator') {
        const bodyDepth = Math.min(0.12, depth);
        const panel = new THREE.Mesh(new THREE.BoxGeometry(width, height, bodyDepth), fill.clone());
        panel.position.set(0, elevation + height / 2, -depth / 2 + bodyDepth / 2);
        panel.castShadow = true;
        panel.receiveShadow = true;
        group.add(panel);

        const ribCount = Math.max(4, Math.min(14, Math.round(width / 0.09)));
        for (let i = 1; i < ribCount; i += 1) {
          const rib = new THREE.Mesh(
            new THREE.BoxGeometry(0.012, Math.max(0.08, height - 0.06), bodyDepth + 0.012),
            lineMat.clone()
          );
          rib.position.set(-width / 2 + (width * i / ribCount), elevation + height / 2, -depth / 2 + bodyDepth / 2 + 0.006);
          group.add(rib);
        }
      } else {
        const block = new THREE.Mesh(
          new THREE.BoxGeometry(width, height, depth),
          fill.clone()
        );
        block.position.set(0, elevation + height / 2, 0);
        block.castShadow = true;
        block.receiveShadow = true;
        group.add(block);
      }

      const labelText = kind === 'door'
        ? 'DOOR'
        : kind === 'window'
          ? 'WINDOW'
          : kind === 'radiator'
            ? 'RADIATOR'
            : kind === 'fridge'
              ? 'FRIDGE / FREEZER'
              : 'OBSTACLE';
      const label = this.makeLabelSprite(labelText, '#25292d', '#ffffff');
      label.position.set(0, Math.min(this.roomHeight - 0.08, elevation + height + 0.12), 0);
      label.scale.set(kind === 'door' ? 0.25 : kind === 'fridge' ? 0.42 : 0.30, 0.085, 1);
      group.add(label);

      const rect = this.unitRect(unit, product);
      const roomW = Number(data.roomWidth || 4000);
      const roomD = Number(data.roomDepth || 3000);
      const cx = rect.x + rect.width / 2;
      const cz = rect.y + rect.depth / 2;
      group.position.x = (cx - roomW / 2) / 1000;
      group.position.z = (cz - roomD / 2) / 1000;

      const edge = this.nearestEdge(rect, roomW, roomD);
      const expectedRotation = (edge === 'left' || edge === 'right') ? 90 : 0;
      const actualRotation = Number(unit.rotation) === 90 ? 90 : 0;
      const extraRotation = (actualRotation - expectedRotation) * Math.PI / 180;
      group.rotation.y = this.rotationForEdge(edge) + extraRotation;

      group.traverse(child => {
        if (!child.userData.unitId && child.userData.kind !== 'room') {
          child.userData.unitId = String(unit.uid);
        }
      });

      return group;
    }

    unitRect(unit, product) {
      const rotated = Number(unit.rotation) === 90;
      const planningDepth = this.fittedRunDepth(product) || Number(product.depth) || 0;
      const width = rotated ? planningDepth : Number(product.width);
      const depth = rotated ? Number(product.width) : planningDepth;
      return {
        x: Number(unit.x) || 0,
        y: Number(unit.y) || 0,
        width,
        depth
      };
    }

    nearestEdge(rect, roomW, roomD) {
      const distances = {
        top: Math.max(0, rect.y),
        bottom: Math.max(0, roomD - (rect.y + rect.depth)),
        left: Math.max(0, rect.x),
        right: Math.max(0, roomW - (rect.x + rect.width))
      };
      return Object.entries(distances).sort((a, b) => a[1] - b[1])[0][0];
    }

    nearestCorner(rect, roomW, roomD) {
      const centreX = rect.x + rect.width / 2;
      const centreZ = rect.y + rect.depth / 2;
      const horizontal = centreX <= roomW / 2 ? 'left' : 'right';
      const vertical = centreZ <= roomD / 2 ? 'top' : 'bottom';
      return `${vertical}-${horizontal}`;
    }

    rotationForEdge(edge) {
      if (edge === 'bottom') return Math.PI;
      if (edge === 'left') return Math.PI / 2;
      if (edge === 'right') return -Math.PI / 2;
      return 0;
    }

    wallBottom(product) {
      if (product.category !== 'wall') return 0;
      // Standard 720mm wall units and 400mm bridging units align at the top.
      return this.isBridgingUnit(product) ? 1.77 : 1.45;
    }

    isAdjustable(product) {
      return /^AL/.test(String(product.sku || '').toUpperCase());
    }

    isUtilitySink(product) {
      return /^TS\d+/.test(String(product.sku || '').toUpperCase());
    }

    isFittedBaseRun(product) {
      if (!product || product.custom || product.category === 'wall' || product.category === 'tall') return false;
      if (product.cornerShape || product.category === 'corner') return false;
      if (this.isUtilitySink(product) || product.standalone) return false;
      return ['base', 'drawer', 'sink', 'oven'].includes(product.category);
    }

    fittedRunDepth(product) {
      return this.isFittedBaseRun(product)
        ? 600
        : Math.max(0, Number(product?.depth) || 0);
    }

    defaultServiceGap(product) {
      if (!this.isFittedBaseRun(product) || this.isAdjustable(product)) return 0;
      return Math.max(0, 578 - (Number(product?.depth) || 0));
    }

    serviceGapForUnit(unit, product) {
      if (!this.isFittedBaseRun(product) || this.isAdjustable(product)) return 0;
      if (this.hasWorktop(product)) return this.defaultServiceGap(product);

      const rawOverride = unit?.serviceGapOverride;
      if (rawOverride !== null && rawOverride !== undefined && rawOverride !== '') {
        const override = Number(rawOverride);
        if (Number.isFinite(override)) {
          return Math.min(this.defaultServiceGap(product), Math.max(0, override));
        }
      }
      return this.defaultServiceGap(product);
    }

    isBridgingUnit(product) {
      const sku = String(product?.sku || '').toUpperCase();
      return product?.familyLabel === 'Bridging wall unit' || /^(?:W)?EX\d/.test(sku);
    }

    supportsDoorOpen(product) {
      return Boolean(product && !product.cornerShape && ['base', 'wall', 'sink', 'tall'].includes(product.category));
    }

    shelfCount(product) {
      if (!product) return 0;
      if (this.isBridgingUnit(product)) return 0;
      if (this.isUtilitySink(product)) return 1;
      if (product.category === 'tall') return 5;
      if (product.category === 'sink' || product.category === 'oven' || product.category === 'drawer') return 0;
      if (['base', 'wall', 'corner'].includes(product.category)) return 1;
      return 0;
    }

    plinthHeight(product) {
      if (!product || product.category === 'wall') return 0;
      if (this.isAdjustable(product)) return 0.15;
      if (this.isUtilitySink(product)) return 0.15;
      return 0.09;
    }

    hasWorktop(product) {
      const title = `${product?.productTitle || ''} ${product?.variantTitle || ''}`;
      const sku = String(product?.sku || '').toUpperCase();
      // The last W is the included-worktop marker; the colour code comes first.
      const includedSku = /^(?:AL)?(?:B|DR|S|O)\d{3,4}(?:WGL|DG|G|W|S|N|C)W$/.test(sku);
      return this.isUtilitySink(product) ||
        includedSku ||
        /pre[- ]?cut worktop|worktop included|includes? (?:a )?worktop|with\s*worktop/i.test(title);
    }

    worktopDepth(product) {
      if (this.isUtilitySink(product)) return 590;
      return 600;
    }

    worktopThickness(product) {
      if (this.isUtilitySink(product)) return 22;
      return 38;
    }

    frontMaterial(product) {
      const THREE = this.THREE;
      const colour = this.colourHex(product.colour);
      const gloss = /gloss/i.test(String(product.colour || ''));
      return new THREE.MeshPhysicalMaterial({
        color: colour,
        roughness: gloss ? 0.23 : 0.66,
        metalness: 0,
        clearcoat: gloss ? 0.62 : 0.05,
        clearcoatRoughness: gloss ? 0.18 : 0.6
      });
    }

    colourHex(name) {
      const value = String(name || '').toLowerCase();
      if (value.includes('navy')) return 0x24364f;
      if (value.includes('sage')) return 0xa9b7a0;
      if (value.includes('cashmere')) return 0xc8bdad;
      if (value.includes('dark grey')) return 0x4a4d4e;
      if (value.includes('grey')) return 0x92928f;
      if (value.includes('white')) return 0xf7f6f2;
      return 0xe9e7df;
    }

    addCarcassInterior(group, width, bodyHeight, depth, bodyBottom, material, uid, product) {
      const THREE = this.THREE;
      const panel = 0.016;
      const innerWidth = Math.max(0.05, width - panel * 2);
      const innerDepth = Math.max(0.05, depth - panel * 1.5);
      const centreY = bodyBottom + bodyHeight / 2;

      const backMaterial = new THREE.MeshStandardMaterial({
        color: 0xfbfaf7,
        roughness: 0.74,
        metalness: 0
      });
      const back = new THREE.Mesh(
        new THREE.BoxGeometry(innerWidth, Math.max(0.05, bodyHeight - panel * 2), panel),
        backMaterial
      );
      back.position.set(0, centreY, -depth / 2 + panel / 2);
      back.receiveShadow = true;
      back.userData.unitId = String(uid);
      group.add(back);

      this.addTopStructure(
        group,
        width,
        bodyHeight,
        depth,
        bodyBottom,
        material,
        uid,
        product
      );

      const bottom = new THREE.Mesh(
        new THREE.BoxGeometry(innerWidth, panel, innerDepth),
        material.clone()
      );
      bottom.position.set(0, bodyBottom + panel / 2, 0);
      bottom.castShadow = true;
      bottom.receiveShadow = true;
      bottom.userData.unitId = String(uid);
      group.add(bottom);

      // JD Greta 1000mm standard base and wall cabinets use a narrow
      // front centre stile/rail, not a full-depth internal divider.
      const hasCentreRail =
        width >= 0.995 &&
        (['base', 'wall'].includes(product.category) || this.isUtilitySink(product)) &&
        !this.isBridgingUnit(product);

      if (hasCentreRail) {
        const railWidth = 0.060;
        const railDepth = 0.018;
        const rail = new THREE.Mesh(
          new THREE.BoxGeometry(
            railWidth,
            Math.max(0.05, bodyHeight - panel * 2),
            railDepth
          ),
          material.clone()
        );
        rail.position.set(
          0,
          centreY,
          depth / 2 - railDepth / 2 - 0.004
        );
        rail.castShadow = true;
        rail.receiveShadow = true;
        rail.userData.unitId = String(uid);
        group.add(rail);
      }

      const shelfCount = this.shelfCount(product);
      for (let i = 1; i <= shelfCount; i++) {
        const shelfY = bodyBottom + bodyHeight * (i / (shelfCount + 1));

        // The 1000mm centre rail is only at the front; the shelf remains a
        // full-width shelf behind it.
        const shelf = new THREE.Mesh(
          new THREE.BoxGeometry(
            innerWidth,
            panel,
            Math.max(0.05, innerDepth - 0.018)
          ),
          material.clone()
        );
        shelf.position.set(0, shelfY, -0.008);
        shelf.castShadow = true;
        shelf.receiveShadow = true;
        shelf.userData.unitId = String(uid);
        group.add(shelf);

        // Five 32mm-style adjustment positions around the nominal shelf height.
        this.addShelfPinHoles(
          group,
          width,
          depth,
          shelfY,
          bodyBottom,
          bodyHeight,
          uid
        );
      }
    }

    topStructureType(product) {
      const sku = String(product?.sku || '').toUpperCase();

      // Exact JD Greta exceptions.
      if (/^BC800/.test(sku)) return 'solid';
      if (/^ALBC900/.test(sku)) return 'albc-corner';
      if (product?.category === 'oven') return 'oven-back-rail';

      // Wall and tall cabinets retain a normal solid top panel.
      if (product?.category === 'wall' || product?.category === 'tall') return 'solid';

      // All other floor cabinets use front + rear 60mm rails:
      // base, adjustable base, drawer, sink and utility-sink families.
      return 'front-back-rails';
    }

    addTopStructure(group, width, bodyHeight, depth, bodyBottom, material, uid, product) {
      const THREE = this.THREE;
      const panel = 0.016;
      const railDepth = 0.060;
      const innerWidth = Math.max(0.05, width - panel * 2);
      const innerDepth = Math.max(0.05, depth - panel * 1.5);
      const topY = bodyBottom + bodyHeight - panel / 2;
      const type = this.topStructureType(product);

      const addPanel = (w, d, x, z) => {
        const part = new THREE.Mesh(
          new THREE.BoxGeometry(Math.max(0.025, w), panel, Math.max(0.025, d)),
          material.clone()
        );
        part.position.set(x, topY, z);
        part.castShadow = true;
        part.receiveShadow = true;
        part.userData.unitId = String(uid);
        group.add(part);
        return part;
      };

      if (type === 'solid') {
        addPanel(innerWidth, innerDepth, 0, 0);
        return;
      }

      if (type === 'oven-back-rail') {
        addPanel(
          innerWidth,
          Math.min(railDepth, innerDepth),
          0,
          -depth / 2 + panel + Math.min(railDepth, innerDepth) / 2
        );
        return;
      }

      if (type === 'albc-corner') {
        // ALBC900 has its own open corner top-frame arrangement rather than
        // a full top board. The two perpendicular rails preserve the open
        // centre visible in the real cabinet.
        const rail = Math.min(railDepth, innerDepth);
        addPanel(
          innerWidth,
          rail,
          0,
          -depth / 2 + panel + rail / 2
        );
        addPanel(
          rail,
          Math.max(0.05, innerDepth - rail),
          width / 2 - panel - rail / 2,
          rail / 2
        );
        return;
      }

      // Standard JD Greta floor construction: two approximately 60mm rails,
      // one at the front and one at the rear, with the middle left open.
      const rail = Math.min(railDepth, innerDepth / 2);
      addPanel(
        innerWidth,
        rail,
        0,
        depth / 2 - panel - rail / 2
      );
      addPanel(
        innerWidth,
        rail,
        0,
        -depth / 2 + panel + rail / 2
      );
    }

    addShelfPinHoles(group, width, depth, shelfY, bodyBottom, bodyHeight, uid, options = {}) {
      const THREE = this.THREE;
      const holeMaterial = new THREE.MeshStandardMaterial({
        color: 0x8d9194,
        roughness: 0.42,
        metalness: 0.18
      });
      const radius = 0.0026;
      const depthDisc = 0.0022;
      const spacing = 0.032;
      const zRows = [
        depth / 2 - 0.055,
        -depth / 2 + 0.060
      ];

      const makeHole = (x, y, z, face) => {
        const hole = new THREE.Mesh(
          new THREE.CylinderGeometry(radius, radius, depthDisc, 10),
          holeMaterial.clone()
        );
        hole.rotation.z = Math.PI / 2;
        hole.position.set(x, y, z);
        hole.userData.unitId = String(uid);
        group.add(hole);
      };

      for (let step = -2; step <= 2; step++) {
        const y = Math.max(
          bodyBottom + 0.045,
          Math.min(bodyBottom + bodyHeight - 0.045, shelfY + step * spacing)
        );

        for (const z of zRows) {
          makeHole(-width / 2 + 0.0175, y, z, 'left');
          makeHole(width / 2 - 0.0175, y, z, 'right');
        }
      }
    }

    addAdjustableLegs(group, width, depth, legHeight, material, uid) {
      const THREE = this.THREE;
      const radius = 0.027;
      const legGeo = new THREE.CylinderGeometry(radius, radius * 1.08, legHeight, 12);
      const insetX = Math.min(0.09, width * 0.18);
      const insetZ = Math.min(0.09, depth * 0.18);
      const xs = [-width / 2 + insetX, width / 2 - insetX];
      const zs = [-depth / 2 + insetZ, depth / 2 - insetZ];
      for (const x of xs) {
        for (const z of zs) {
          const leg = new THREE.Mesh(legGeo, material.clone());
          leg.position.set(x, legHeight / 2, z);
          leg.castShadow = true;
          leg.userData.unitId = String(uid);
          group.add(leg);
        }
      }
    }

    addAppliedPlinthRuns3D(data) {
      const THREE = this.THREE;
      const runs = Array.isArray(data?.plinthRuns) ? data.plinthRuns : [];
      if (!runs.length) return;

      const roomW = Number(data.roomWidth || 4000);
      const roomD = Number(data.roomDepth || 3000);

      for (const run of runs) {
        const lengthMm = Math.max(100, Math.min(10000, Math.round(Number(run.length) || 0)));
        if (!lengthMm) continue;

        const plinthProduct = data?.productById?.get
          ? data.productById.get(String(run.productId || ''))
          : (data?.catalog || []).find(item => String(item.id) === String(run.productId || ''));
        const material = this.frontMaterial(plinthProduct || { colour: run.colour || 'White' });

        const length = lengthMm / 1000;
        const height = 0.15;
        const thickness = 0.020;
        const edge = String(run.edge || 'bottom');
        const start = Number(run.start) || 0;
        const front = Number(run.front) || 0;

        const mesh = new THREE.Mesh(
          (edge === 'top' || edge === 'bottom')
            ? new THREE.BoxGeometry(length, height - 0.004, thickness)
            : new THREE.BoxGeometry(thickness, height - 0.004, length),
          material
        );

        if (edge === 'top' || edge === 'bottom') {
          mesh.position.x = (start + lengthMm / 2 - roomW / 2) / 1000;
          mesh.position.z = (front - roomD / 2) / 1000;
        } else {
          mesh.position.x = (front - roomW / 2) / 1000;
          mesh.position.z = (start + lengthMm / 2 - roomD / 2) / 1000;
        }
        mesh.position.y = height / 2;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData.appliedPlinth = true;
        mesh.userData.plinthRun = true;
        mesh.userData.unitId = String(run.anchorUnitId || '');
        mesh.userData.plinthLength = lengthMm;
        this.modelGroup.add(mesh);

        if (run.selected) {
          const label = this.makeLabelSprite(
            `${lengthMm} mm PLINTH · ${(run.unitIds || []).length} CABINET${(run.unitIds || []).length === 1 ? '' : 'S'} · CUT FROM 2700`,
            '#111111',
            '#ffffff'
          );

          if (edge === 'top' || edge === 'bottom') {
            label.position.set(
              (start + Math.min(lengthMm, 900) / 2 - roomW / 2) / 1000,
              height + 0.085,
              (front - roomD / 2) / 1000
            );
          } else {
            label.position.set(
              (front - roomW / 2) / 1000,
              height + 0.085,
              (start + Math.min(lengthMm, 900) / 2 - roomD / 2) / 1000
            );
          }
          label.scale.set(Math.min(0.9, Math.max(0.52, length * 0.34)), 0.09, 1);
          label.userData.appliedPlinth = true;
          label.userData.plinthRun = true;
          label.userData.unitId = String(run.anchorUnitId || '');
          this.modelGroup.add(label);
        }
      }
    }

    addPlinth(group, width, depth, height, frontMat, uid, options = {}) {
      const THREE = this.THREE;

      // White plinth is supplied as standard. When the user applies the optional
      // 2.7 m colour-matched AL plinth, render the fitted section in door colour.
      const plinthMaterial = options.colourMatched
        ? frontMat.clone()
        : new THREE.MeshStandardMaterial({
            color: 0xf7f7f4,
            roughness: 0.68,
            metalness: 0
          });

      const recess = options.adjustable
        ? 0.045
        : (options.utilitySink ? 0.042 : 0.034);
      const thickness = 0.016;
      const frontZ = depth / 2 - recess;
      const sideAllowance = options.adjustable ? 0.018 : 0.032;

      const plinth = new THREE.Mesh(
        new THREE.BoxGeometry(
          Math.max(0.05, width - sideAllowance),
          Math.max(0.04, height - 0.006),
          thickness
        ),
        plinthMaterial
      );
      plinth.position.set(0, height / 2, frontZ);
      plinth.castShadow = true;
      plinth.receiveShadow = true;
      plinth.userData.unitId = String(uid);
      group.add(plinth);
    }

    hingePositions(panelH) {
      const count = panelH > 1.05 ? 3 : 2;
      const inset = Math.min(0.12, Math.max(0.075, panelH * 0.17));
      return count === 3
        ? [-panelH / 2 + inset, 0, panelH / 2 - inset]
        : [-panelH / 2 + inset, panelH / 2 - inset];
    }

    hingeMaterials() {
      const THREE = this.THREE;

      // Keep metalness deliberately modest. Without an HDR environment,
      // highly metallic PBR materials render too dark on mobile and make the
      // hinge look like a black plastic pin.
      const silver = new THREE.MeshStandardMaterial({
        color: 0xbfc4c7,
        roughness: 0.30,
        metalness: 0.28
      });
      const silverMid = new THREE.MeshStandardMaterial({
        color: 0x92999d,
        roughness: 0.34,
        metalness: 0.30
      });
      const screw = new THREE.MeshStandardMaterial({
        color: 0x676e72,
        roughness: 0.38,
        metalness: 0.32
      });

      return { silver, silverMid, screw };
    }

    addDoorHinges(pivot, panelW, panelH, direction, material, uid) {
      const THREE = this.THREE;
      const ys = this.hingePositions(panelH);
      const mats = this.hingeMaterials();

      for (const y of ys) {
        // Standard 35mm concealed-hinge cup located in the rear face of the
        // door, roughly 22mm from its hinged edge.
        const cupX = direction * 0.023;
        const rearZ = -0.0114;

        // Outer cup/flange.
        const cupOuter = new THREE.Mesh(
          new THREE.CylinderGeometry(0.0175, 0.0175, 0.0025, 28),
          mats.silver.clone()
        );
        cupOuter.rotation.x = Math.PI / 2;
        cupOuter.position.set(cupX, y, rearZ);
        cupOuter.castShadow = true;
        cupOuter.userData.unitId = String(uid);
        pivot.add(cupOuter);

        // Shallow recessed bowl, still silver rather than a black hole.
        const cupBowl = new THREE.Mesh(
          new THREE.CylinderGeometry(0.0120, 0.0120, 0.0028, 28),
          mats.silverMid.clone()
        );
        cupBowl.rotation.x = Math.PI / 2;
        cupBowl.position.set(cupX, y, rearZ - 0.0010);
        cupBowl.userData.unitId = String(uid);
        pivot.add(cupBowl);

        // Two small wings either side of the cup, similar to a real concealed
        // hinge flange.
        for (const dy of [-0.0135, 0.0135]) {
          const wing = new THREE.Mesh(
            new THREE.BoxGeometry(0.030, 0.008, 0.0028),
            mats.silver.clone()
          );
          wing.position.set(cupX, y + dy, rearZ - 0.0002);
          wing.castShadow = true;
          wing.userData.unitId = String(uid);
          pivot.add(wing);

          const fixing = new THREE.Mesh(
            new THREE.CylinderGeometry(0.0020, 0.0020, 0.0012, 12),
            mats.screw.clone()
          );
          fixing.rotation.x = Math.PI / 2;
          fixing.position.set(cupX, y + dy, rearZ - 0.0019);
          fixing.userData.unitId = String(uid);
          pivot.add(fixing);
        }

        // Compact neck from the door cup toward the hinge pivot.
        const neck = new THREE.Mesh(
          new THREE.BoxGeometry(0.026, 0.012, 0.006),
          mats.silver.clone()
        );
        neck.position.set(direction * 0.006, y, rearZ - 0.0026);
        neck.castShadow = true;
        neck.userData.unitId = String(uid);
        pivot.add(neck);
      }
    }

    addCabinetHingeMounts(group, panelH, centreY, hingeX, frontZ, hingeSide, uid) {
      const THREE = this.THREE;
      const ys = this.hingePositions(panelH);
      const side = hingeSide === 'right' ? -1 : 1;
      const mats = this.hingeMaterials();

      for (const localY of ys) {
        const y = centreY + localY;
        const sideX = hingeX + side * 0.0105;

        // Slim rectangular mounting plate on the inside face of the cabinet
        // side panel. This is the part missing visually in the earlier model.
        const plate = new THREE.Mesh(
          new THREE.BoxGeometry(0.0040, 0.032, 0.050),
          mats.silver.clone()
        );
        plate.position.set(sideX, y, frontZ - 0.043);
        plate.castShadow = true;
        plate.userData.unitId = String(uid);
        group.add(plate);

        // Raised central runner of the mounting plate.
        const runner = new THREE.Mesh(
          new THREE.BoxGeometry(0.0070, 0.014, 0.037),
          mats.silverMid.clone()
        );
        runner.position.set(
          hingeX + side * 0.0075,
          y,
          frontZ - 0.036
        );
        runner.castShadow = true;
        runner.userData.unitId = String(uid);
        group.add(runner);

        // Articulated arm projecting from the mounting plate toward the door.
        // Wider and lighter than before so it reads as hinge hardware, not an arrow.
        const arm = new THREE.Mesh(
          new THREE.BoxGeometry(0.010, 0.013, 0.040),
          mats.silver.clone()
        );
        arm.position.set(
          hingeX + side * 0.0055,
          y,
          frontZ - 0.014
        );
        arm.castShadow = true;
        arm.userData.unitId = String(uid);
        group.add(arm);

        // Rounded pivot block at the front end of the arm.
        const pivotBarrel = new THREE.Mesh(
          new THREE.CylinderGeometry(0.0060, 0.0060, 0.013, 18),
          mats.silverMid.clone()
        );
        pivotBarrel.rotation.z = Math.PI / 2;
        pivotBarrel.position.set(
          hingeX + side * 0.0055,
          y,
          frontZ + 0.004
        );
        pivotBarrel.castShadow = true;
        pivotBarrel.userData.unitId = String(uid);
        group.add(pivotBarrel);

        // Two visible fixing screws on the mounting plate.
        for (const screwZ of [frontZ - 0.058, frontZ - 0.030]) {
          const screw = new THREE.Mesh(
            new THREE.CylinderGeometry(0.0022, 0.0022, 0.0016, 12),
            mats.screw.clone()
          );
          screw.rotation.z = Math.PI / 2;
          screw.position.set(
            hingeX + side * 0.0085,
            y,
            screwZ
          );
          screw.userData.unitId = String(uid);
          group.add(screw);
        }
      }
    }

    addDoorLeaf(group, options) {
      const THREE = this.THREE;
      const {
        panelW,
        panelH,
        hingeX,
        centreY,
        frontZ,
        hingeSide,
        open,
        frontMat,
        metalMat,
        uid,
        handleYOffset = 0
      } = options;

      const pivot = new THREE.Group();
      pivot.position.set(hingeX, centreY, frontZ);
      pivot.userData.unitId = String(uid);

      const direction = hingeSide === 'right' ? -1 : 1;

      // Keep the real door colour on the face, but give the rear/edges enough
      // tonal separation to remain readable when a white door opens against a
      // pale room wall.
      const faceMaterial = frontMat.clone();
      const rearMaterial = frontMat.clone();
      if (rearMaterial.color?.multiplyScalar) rearMaterial.color.multiplyScalar(0.90);
      rearMaterial.roughness = Math.min(0.82, Number(rearMaterial.roughness ?? 0.65) + 0.12);
      if ('clearcoat' in rearMaterial) rearMaterial.clearcoat = Math.min(0.12, Number(rearMaterial.clearcoat || 0));

      const edgeMaterial = frontMat.clone();
      if (edgeMaterial.color?.multiplyScalar) edgeMaterial.color.multiplyScalar(0.84);
      edgeMaterial.roughness = Math.min(0.86, Number(edgeMaterial.roughness ?? 0.68) + 0.16);
      if ('clearcoat' in edgeMaterial) edgeMaterial.clearcoat = 0;

      const panelGeometry = new THREE.BoxGeometry(panelW, panelH, 0.020);
      const panel = new THREE.Mesh(
        panelGeometry,
        [
          edgeMaterial.clone(),
          edgeMaterial.clone(),
          edgeMaterial.clone(),
          edgeMaterial.clone(),
          faceMaterial,
          rearMaterial
        ]
      );
      panel.position.set(direction * panelW / 2, 0, 0);
      panel.castShadow = true;
      panel.receiveShadow = true;
      panel.userData.unitId = String(uid);
      pivot.add(panel);

      if (open) {
        const outline = new THREE.LineSegments(
          new THREE.EdgesGeometry(panelGeometry, 28),
          new THREE.LineBasicMaterial({
            color: 0x777a78,
            transparent: true,
            opacity: 0.30
          })
        );
        outline.position.copy(panel.position);
        outline.userData.unitId = String(uid);
        pivot.add(outline);
      }

      const handleLength = Math.min(0.16, Math.max(0.105, panelH * 0.20));
      const handleX = direction * (panelW - 0.040);
      this.addBarHandle(
        pivot,
        handleX,
        handleYOffset,
        0.018,
        handleLength,
        'vertical',
        metalMat,
        uid
      );

      if (open) {
        this.addDoorHinges(
          pivot,
          panelW,
          panelH,
          direction,
          metalMat,
          uid
        );
        this.addCabinetHingeMounts(
          group,
          panelH,
          centreY,
          hingeX,
          frontZ,
          hingeSide,
          uid
        );
        pivot.rotation.y = hingeSide === 'right'
          ? Math.PI * 0.58
          : -Math.PI * 0.58;
      }

      group.add(pivot);
      return pivot;
    }

    addExtractorHood(group, width, depth, bodyBottom, metalMat, darkMat, uid) {
      const THREE = this.THREE;
      // Keep the extractor assembly within the standard wall-unit depth.
      const canopyDepth = depth;
      const canopyWidth = Math.max(0.4, width + 0.025);
      const canopyY = bodyBottom - 0.026;
      const frontOffset = 0;

      const shellMat = new THREE.MeshPhysicalMaterial({
        color: 0xf0f1ef,
        roughness: 0.34,
        metalness: 0.18,
        clearcoat: 0.25,
        clearcoatRoughness: 0.22
      });

      const canopy = new THREE.Mesh(
        new THREE.BoxGeometry(canopyWidth, 0.045, canopyDepth),
        shellMat
      );
      canopy.position.set(0, canopyY, frontOffset);
      canopy.castShadow = true;
      canopy.receiveShadow = true;
      canopy.userData.unitId = String(uid);
      group.add(canopy);

      const filter = new THREE.Mesh(
        new THREE.BoxGeometry(Math.max(0.24, width * 0.62), 0.008, Math.max(0.18, canopyDepth * 0.46)),
        darkMat.clone()
      );
      filter.position.set(0, canopyY - 0.027, frontOffset + canopyDepth * 0.02);
      filter.userData.unitId = String(uid);
      group.add(filter);

      const lip = new THREE.Mesh(
        new THREE.BoxGeometry(canopyWidth * 0.92, 0.026, 0.025),
        metalMat.clone()
      );
      lip.position.set(0, canopyY - 0.002, frontOffset + canopyDepth / 2 - 0.012);
      lip.userData.unitId = String(uid);
      group.add(lip);

      const buttonGeometry = new THREE.BoxGeometry(0.018, 0.012, 0.012);
      for (let index = 0; index < 4; index += 1) {
        const button = new THREE.Mesh(buttonGeometry, metalMat.clone());
        button.position.set(
          (index - 1.5) * 0.028,
          canopyY - 0.015,
          frontOffset + canopyDepth / 2 + 0.004
        );
        button.userData.unitId = String(uid);
        group.add(button);
      }
    }

    addLiftUpDoor(group, width, bodyHeight, depth, bodyBottom, frontMat, metalMat, uid, open) {
      const THREE = this.THREE;
      const gap = 0.006;
      const panelW = Math.max(0.12, width - gap * 2);
      const panelH = Math.max(0.12, bodyHeight - gap * 2);
      const frontZ = depth / 2 + 0.011;

      // Extractor/bridging cabinets use one horizontal flap hinged along the
      // top edge, not a normal side-hinged wall-unit door.
      const pivot = new THREE.Group();
      pivot.position.set(0, bodyBottom + bodyHeight - gap, frontZ);
      pivot.userData.unitId = String(uid);

      const faceMaterial = frontMat.clone();
      const rearMaterial = frontMat.clone();
      if (rearMaterial.color?.multiplyScalar) rearMaterial.color.multiplyScalar(0.90);
      const edgeMaterial = frontMat.clone();
      if (edgeMaterial.color?.multiplyScalar) edgeMaterial.color.multiplyScalar(0.84);

      const geometry = new THREE.BoxGeometry(panelW, panelH, 0.020);
      const panel = new THREE.Mesh(geometry, [
        edgeMaterial.clone(),
        edgeMaterial.clone(),
        edgeMaterial.clone(),
        edgeMaterial.clone(),
        faceMaterial,
        rearMaterial
      ]);
      panel.position.set(0, -panelH / 2, 0);
      panel.castShadow = true;
      panel.receiveShadow = true;
      panel.userData.unitId = String(uid);
      pivot.add(panel);

      this.addBarHandle(
        pivot,
        0,
        -panelH + Math.min(0.060, panelH * 0.18),
        0.018,
        Math.min(0.22, Math.max(0.13, panelW * 0.38)),
        'horizontal',
        metalMat,
        uid
      );

      if (open) {
        pivot.rotation.x = -Math.PI * 0.52;
      }

      group.add(pivot);
      return pivot;
    }

    addDoors(group, width, bodyHeight, depth, bodyBottom, frontMat, metalMat, uid, product, unit = {}) {
      const gap = 0.005;
      const frontZ = depth / 2 + 0.011;
      const panelH = Math.max(0.12, bodyHeight - 0.006);
      const bridge = this.isBridgingUnit(product);
      const doorCount = width >= 0.595 ? 2 : 1;
      const panelW = (width - gap * (doorCount + 1)) / doorCount;
      const centreY = bodyBottom + bodyHeight / 2;
      const open = Boolean(unit.doorOpen);

      if (bridge) {
        this.addLiftUpDoor(
          group,
          width,
          bodyHeight,
          depth,
          bodyBottom,
          frontMat,
          metalMat,
          uid,
          open
        );
        return;
      }

      if (doorCount === 1) {
        const hingeSide = unit.hingeSide === 'right' ? 'right' : 'left';
        const hingeX = hingeSide === 'right'
          ? width / 2 - gap
          : -width / 2 + gap;
        this.addDoorLeaf(group, {
          panelW,
          panelH,
          hingeX,
          centreY,
          frontZ,
          hingeSide,
          open,
          frontMat,
          metalMat,
          uid
        });
        return;
      }

      this.addDoorLeaf(group, {
        panelW,
        panelH,
        hingeX: -width / 2 + gap,
        centreY,
        frontZ,
        hingeSide: 'left',
        open,
        frontMat,
        metalMat,
        uid
      });
      this.addDoorLeaf(group, {
        panelW,
        panelH,
        hingeX: width / 2 - gap,
        centreY,
        frontZ,
        hingeSide: 'right',
        open,
        frontMat,
        metalMat,
        uid
      });
    }

    addDrawers(group, width, bodyHeight, depth, bodyBottom, frontMat, metalMat, uid) {
      const THREE = this.THREE;
      const frontZ = depth / 2 + 0.011;
      const proportions = [0.18, 0.18, 0.18, 0.46];
      const usable = bodyHeight - 0.03;
      let y = bodyBottom + bodyHeight;

      for (let i = 0; i < proportions.length; i++) {
        const h = usable * proportions[i] - 0.004;
        y -= h / 2 + (i ? 0.004 : 0.008);
        const panel = new THREE.Mesh(
          new THREE.BoxGeometry(width - 0.012, Math.max(0.055, h), 0.02),
          frontMat.clone()
        );
        panel.position.set(0, y, frontZ);
        panel.castShadow = true;
        panel.userData.unitId = String(uid);
        group.add(panel);

        this.addBarHandle(
          group,
          0,
          y + h * 0.22,
          frontZ + 0.020,
          Math.min(0.18, Math.max(0.11, width * 0.38)),
          'horizontal',
          metalMat,
          uid
        );

        y -= h / 2;
      }
    }

    addTallFront(group, width, bodyHeight, depth, bodyBottom, frontMat, metalMat, uid, product, unit = {}) {
      const frontZ = depth / 2 + 0.011;
      const split = Math.min(0.715, bodyHeight * 0.38);
      const topH = bodyHeight - split - 0.008;
      const lowerH = split - 0.008;
      const panelW = width - 0.012;
      const hingeSide = unit.hingeSide === 'right' ? 'right' : 'left';
      const hingeX = hingeSide === 'right' ? width / 2 - 0.006 : -width / 2 + 0.006;
      const open = Boolean(unit.doorOpen);

      this.addDoorLeaf(group, {
        panelW,
        panelH: topH,
        hingeX,
        centreY: bodyBottom + split + 0.008 + topH / 2,
        frontZ,
        hingeSide,
        open,
        frontMat,
        metalMat,
        uid,
        handleYOffset: -Math.min(0.18, topH * 0.22)
      });

      this.addDoorLeaf(group, {
        panelW,
        panelH: lowerH,
        hingeX,
        centreY: bodyBottom + lowerH / 2,
        frontZ,
        hingeSide,
        open,
        frontMat,
        metalMat,
        uid,
        handleYOffset: Math.min(0.10, lowerH * 0.16)
      });
    }

    addBarHandle(group, x, y, z, length, orientation, material, uid) {
      const THREE = this.THREE;
      const radius = 0.0065;
      const bar = new THREE.Mesh(
        new THREE.CylinderGeometry(radius, radius, length, 14),
        material.clone()
      );

      if (orientation === 'horizontal') {
        bar.rotation.z = Math.PI / 2;
      }
      bar.position.set(x, y, z + 0.018);
      bar.castShadow = true;
      bar.userData.unitId = String(uid);
      group.add(bar);

      const offsets = [-length * 0.31, length * 0.31];
      for (const offset of offsets) {
        const peg = new THREE.Mesh(
          new THREE.CylinderGeometry(0.0052, 0.0052, 0.024, 12),
          material.clone()
        );
        peg.rotation.x = Math.PI / 2;
        if (orientation === 'horizontal') {
          peg.position.set(x + offset, y, z + 0.009);
        } else {
          peg.position.set(x, y + offset, z + 0.009);
        }
        peg.castShadow = true;
        peg.userData.unitId = String(uid);
        group.add(peg);
      }
    }

    addOvenFront(group, width, bodyHeight, depth, bodyBottom, frontMat, darkMat, metalMat, uid) {
      const THREE = this.THREE;
      const frontZ = depth / 2 + 0.012;
      const drawerH = Math.min(0.15, Math.max(0.12, bodyHeight * 0.21));
      const gap = 0.010;
      const cavityH = Math.max(0.42, bodyHeight - drawerH - gap);

      const drawer = new THREE.Mesh(
        new THREE.BoxGeometry(width - 0.012, drawerH, 0.020),
        frontMat.clone()
      );
      drawer.position.set(0, bodyBottom + drawerH / 2, frontZ);
      drawer.castShadow = true;
      drawer.userData.unitId = String(uid);
      group.add(drawer);

      this.addBarHandle(
        group,
        0,
        drawer.position.y,
        frontZ + 0.020,
        Math.min(0.20, Math.max(0.13, width * 0.34)),
        'horizontal',
        metalMat,
        uid
      );

      const oven = new THREE.Mesh(
        new THREE.BoxGeometry(width - 0.030, cavityH, 0.030),
        darkMat.clone()
      );
      oven.position.set(
        0,
        bodyBottom + drawerH + gap + cavityH / 2,
        frontZ + 0.005
      );
      oven.castShadow = true;
      oven.userData.unitId = String(uid);
      group.add(oven);

      const glass = new THREE.Mesh(
        new THREE.BoxGeometry(width - 0.085, cavityH * 0.62, 0.008),
        new THREE.MeshPhysicalMaterial({
          color: 0x0d1012,
          roughness: 0.10,
          metalness: 0.18,
          transmission: 0.06
        })
      );
      glass.position.set(0, oven.position.y - cavityH * 0.055, frontZ + 0.026);
      glass.userData.unitId = String(uid);
      group.add(glass);

      this.addBarHandle(
        group,
        0,
        oven.position.y + cavityH * 0.34,
        frontZ + 0.035,
        width - 0.11,
        'horizontal',
        metalMat,
        uid
      );
    }

    worktopTexture() {
      if (this._worktopTexture) return this._worktopTexture;

      const THREE = this.THREE;
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#cec8bc';
      ctx.fillRect(0, 0, 256, 256);

      // Deterministic light stone fleck, based on the pale speckled JD Greta
      // worktop shown in the product photography.
      let seed = 1847;
      const rnd = () => {
        seed = (seed * 9301 + 49297) % 233280;
        return seed / 233280;
      };
      const flecks = [
        ['#aaa69e', 360, 0.35, 1.25],
        ['#b9b1a6', 280, 0.45, 1.55],
        ['#8f8e89', 90, 0.30, 0.95],
        ['#e6e1d7', 260, 0.45, 1.45],
        ['#d8d2c7', 220, 0.30, 1.05]
      ];
      for (const [colour, count, minR, maxR] of flecks) {
        ctx.fillStyle = colour;
        for (let i = 0; i < count; i++) {
          const x = rnd() * 256;
          const y = rnd() * 256;
          const r = minR + rnd() * (maxR - minR);
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(2.8, 2.8);
      texture.anisotropy = Math.min(4, this.renderer?.capabilities?.getMaxAnisotropy?.() || 1);
      this._worktopTexture = texture;
      return texture;
    }

    worktopMaterial() {
      const THREE = this.THREE;
      return new THREE.MeshStandardMaterial({
        color: 0xffffff,
        map: this.worktopTexture(),
        roughness: 0.64,
        metalness: 0.02
      });
    }

    addWorktop(group, width, depth, totalHeight, product, uid) {
      const THREE = this.THREE;
      const worktopDepth = this.worktopDepth(product) / 1000;
      const thickness = this.worktopThickness(product) / 1000;
      const fittedRun = this.isFittedBaseRun(product);
      const frontOverhang = this.isUtilitySink(product)
        ? 0.004
        : (this.isAdjustable(product) ? Math.max(0.018, worktopDepth - depth) : 0.050);
      const frontEdge = depth / 2 + frontOverhang;
      const z = fittedRun ? 0 : (frontEdge - worktopDepth / 2);
      const material = this.worktopMaterial();

      const addSlab = (partWidth, partDepth, x, partZ) => {
        if (partWidth <= 0.002 || partDepth <= 0.002) return;
        const slab = new THREE.Mesh(
          new THREE.BoxGeometry(partWidth, thickness, partDepth),
          material.clone()
        );
        slab.position.set(x, totalHeight + thickness / 2, partZ);
        slab.castShadow = true;
        slab.receiveShadow = true;
        slab.userData.unitId = String(uid);
        group.add(slab);
      };

      if (!this.isUtilitySink(product)) {
        addSlab(width + 0.006, worktopDepth, 0, z);
        return;
      }

      // TS1000 needs a genuine opening below the bowl. Build its worktop as
      // four frame pieces instead of placing a solid slab through the sink.
      const trayW = Math.min(width - 0.06, 0.92);
      const basinW = Math.min(0.40, trayW * 0.44);
      const basinD = 0.34;
      const basinX = trayW * 0.23;
      const basinZ = 0.018;
      const left = -(width + 0.006) / 2;
      const right = (width + 0.006) / 2;
      const back = z - worktopDepth / 2;
      const front = z + worktopDepth / 2;
      const holeLeft = basinX - basinW / 2;
      const holeRight = basinX + basinW / 2;
      const holeBack = basinZ - basinD / 2;
      const holeFront = basinZ + basinD / 2;

      addSlab(holeLeft - left, worktopDepth, (left + holeLeft) / 2, z);
      addSlab(right - holeRight, worktopDepth, (holeRight + right) / 2, z);
      addSlab(basinW, holeBack - back, basinX, (back + holeBack) / 2);
      addSlab(basinW, front - holeFront, basinX, (holeFront + front) / 2);
    }

    addSinkAndTap(group, width, totalHeight, product, uid) {
      const THREE = this.THREE;
      const thickness = this.worktopThickness(product) / 1000;
      const topY = totalHeight + thickness + 0.006;
      const trayW = Math.min(width - 0.06, 0.92);
      const trayD = 0.47;
      const basinW = Math.min(0.40, trayW * 0.44);
      const basinD = 0.34;
      const basinH = 0.17;
      const basinX = trayW * 0.23;
      const basinZ = 0.018;
      const wall = 0.012;
      const steel = new THREE.MeshPhysicalMaterial({
        color: 0xe7e9ea,
        roughness: 0.24,
        metalness: 0.52,
        clearcoat: 0.36,
        clearcoatRoughness: 0.12,
        side: THREE.DoubleSide
      });
      const innerSteel = new THREE.MeshStandardMaterial({
        color: 0xaeb5b8,
        roughness: 0.34,
        metalness: 0.48,
        side: THREE.DoubleSide
      });

      const addPart = (geometry, material, x, y, z) => {
        const part = new THREE.Mesh(geometry, material);
        part.position.set(x, y, z);
        part.castShadow = true;
        part.receiveShadow = true;
        part.userData.unitId = String(uid);
        group.add(part);
        return part;
      };
      const addTubeBetween = (from, to, radius, material) => {
        const direction = new THREE.Vector3().subVectors(to, from);
        const length = direction.length();
        const tube = addPart(
          new THREE.CylinderGeometry(radius, radius, length, 16),
          material,
          (from.x + to.x) / 2,
          (from.y + to.y) / 2,
          (from.z + to.z) / 2
        );
        tube.quaternion.setFromUnitVectors(
          new THREE.Vector3(0, 1, 0),
          direction.normalize()
        );
        return tube;
      };

      // Split stainless deck leaves the bowl physically open from above.
      const trayLeft = -trayW / 2;
      const trayRight = trayW / 2;
      const trayBack = basinZ - trayD / 2;
      const trayFront = basinZ + trayD / 2;
      const bowlLeft = basinX - basinW / 2;
      const bowlRight = basinX + basinW / 2;
      const bowlBack = basinZ - basinD / 2;
      const bowlFront = basinZ + basinD / 2;
      const deckY = topY;

      addPart(new THREE.BoxGeometry(bowlLeft - trayLeft, 0.008, trayD), steel, (trayLeft + bowlLeft) / 2, deckY, basinZ);
      addPart(new THREE.BoxGeometry(trayRight - bowlRight, 0.008, trayD), steel, (bowlRight + trayRight) / 2, deckY, basinZ);
      addPart(new THREE.BoxGeometry(basinW, 0.008, bowlBack - trayBack), steel, basinX, deckY, (trayBack + bowlBack) / 2);
      addPart(new THREE.BoxGeometry(basinW, 0.008, trayFront - bowlFront), steel, basinX, deckY, (bowlFront + trayFront) / 2);

      // Open stainless bowl: four inner walls and a recessed bottom.
      addPart(new THREE.BoxGeometry(basinW - wall * 2, 0.010, basinD - wall * 2), innerSteel, basinX, topY - basinH, basinZ);
      addPart(new THREE.BoxGeometry(wall, basinH, basinD), steel.clone(), basinX - basinW / 2 + wall / 2, topY - basinH / 2, basinZ);
      addPart(new THREE.BoxGeometry(wall, basinH, basinD), steel.clone(), basinX + basinW / 2 - wall / 2, topY - basinH / 2, basinZ);
      addPart(new THREE.BoxGeometry(basinW - wall * 2, basinH, wall), steel.clone(), basinX, topY - basinH / 2, basinZ - basinD / 2 + wall / 2);
      addPart(new THREE.BoxGeometry(basinW - wall * 2, basinH, wall), steel.clone(), basinX, topY - basinH / 2, basinZ + basinD / 2 - wall / 2);

      // Raised drainer ribs on the left deck.
      const drainerX = (trayLeft + bowlLeft) / 2;
      for (let i = -4; i <= 4; i++) {
        const rib = addPart(
          new THREE.BoxGeometry(Math.min(0.31, (bowlLeft - trayLeft) * 0.72), 0.004, 0.008),
          innerSteel.clone(),
          drainerX,
          topY + 0.007,
          basinZ + i * 0.038
        );
        rib.rotation.y = -0.10;
      }

      // Waste fitting and visible pipe inside the open TS1000 cabinet.
      addPart(new THREE.CylinderGeometry(0.034, 0.034, 0.008, 24), steel, basinX, topY - basinH + 0.008, basinZ);
      addPart(
        new THREE.CylinderGeometry(0.026, 0.022, 0.13, 18),
        new THREE.MeshStandardMaterial({ color: 0xd6d8d7, roughness: 0.68 }),
        basinX,
        topY - basinH - 0.065,
        basinZ
      );

      // Compact straight-spout chrome mixer matching the product photograph.
      const tapX = 0.015;
      const tapZ = -trayD / 2 + 0.052;
      addPart(new THREE.CylinderGeometry(0.021, 0.025, 0.016, 20), steel, tapX, topY + 0.012, tapZ);
      addPart(new THREE.CylinderGeometry(0.010, 0.012, 0.090, 16), steel.clone(), tapX, topY + 0.061, tapZ);

      const spoutStart = new THREE.Vector3(tapX, topY + 0.105, tapZ);
      const spoutEnd = new THREE.Vector3(basinX - 0.055, topY + 0.132, tapZ + 0.125);
      addTubeBetween(spoutStart, spoutEnd, 0.008, steel.clone());
      addPart(
        new THREE.CylinderGeometry(0.009, 0.009, 0.035, 16),
        steel.clone(),
        spoutEnd.x,
        spoutEnd.y - 0.017,
        spoutEnd.z
      );

      addTubeBetween(
        new THREE.Vector3(tapX - 0.010, topY + 0.070, tapZ),
        new THREE.Vector3(tapX - 0.044, topY + 0.105, tapZ + 0.004),
        0.0045,
        steel.clone()
      );
    }

    makeRulerLabelSprite(text, colour) {
      const THREE = this.THREE;
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 128;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = '900 64px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 12;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.98)';
      ctx.strokeText(String(text || ''), canvas.width / 2, canvas.height / 2, canvas.width - 28);
      ctx.fillStyle = colour;
      ctx.fillText(String(text || ''), canvas.width / 2, canvas.height / 2, canvas.width - 28);
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
      const sprite = new THREE.Sprite(material);
      sprite.scale.set(0.78, 0.20, 1);
      return sprite;
    }

    makeLabelSprite(text, bg, fg) {
      const THREE = this.THREE;
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 112;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = fg;
      ctx.font = '700 42px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(text || ''), canvas.width / 2, canvas.height / 2, canvas.width - 40);
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
      const sprite = new THREE.Sprite(material);
      sprite.scale.set(0.42, 0.13, 1);
      return sprite;
    }

    setSelected(uid) {
      if (!this.ready || !this.scene) return;

      if (this.selectionHelper) {
        this.scene.remove(this.selectionHelper);
        this.selectionHelper.geometry?.dispose?.();
        this.selectionHelper.material?.dispose?.();
        this.selectionHelper = null;
      }

      if (this.selectionFrontMarker) {
        this.selectionFrontMarker.parent?.remove(this.selectionFrontMarker);
        this.selectionFrontMarker.material?.map?.dispose?.();
        this.selectionFrontMarker.material?.dispose?.();
        this.selectionFrontMarker = null;
      }

      if (this.selectionFrontGuide) {
        this.selectionFrontGuide.parent?.remove(this.selectionFrontGuide);
        this.selectionFrontGuide.geometry?.dispose?.();
        this.selectionFrontGuide.material?.dispose?.();
        this.selectionFrontGuide = null;
      }

      if (this.selectionMoveHandle) {
        this.selectionMoveHandle.parent?.remove(this.selectionMoveHandle);
        this.selectionMoveHandle.material?.map?.dispose?.();
        this.selectionMoveHandle.material?.dispose?.();
        this.selectionMoveHandle = null;
      }

      this.selectedUid = uid ? String(uid) : null;
      if (!uid) return;
      const group = this.groupsByUnit.get(String(uid));
      if (!group) return;

      this.selectionHelper = new this.THREE.BoxHelper(group, 0x111111);
      this.selectionHelper.material.depthTest = false;
      this.selectionHelper.material.transparent = true;
      this.selectionHelper.material.opacity = 0.86;
      this.selectionHelper.renderOrder = 999;
      this.scene.add(this.selectionHelper);

      if (Number.isFinite(group.userData.frontDepth)) {
        const frontDepth = Number(group.userData.frontDepth);
        const frontY = Number(group.userData.frontY) || 0.08;
        const frontWidth = Math.max(0.18, Number(group.userData.frontWidth) || 0.5);

        // A short black strip sits just in front of the selected cabinet.
        // It moves with the cabinet, so an accidental 90° rotation is obvious.
        const guide = new this.THREE.Mesh(
          new this.THREE.BoxGeometry(Math.max(0.16, frontWidth * 0.72), 0.014, 0.018),
          new this.THREE.MeshBasicMaterial({
            color: 0x111111,
            depthTest: false,
            depthWrite: false
          })
        );
        guide.position.set(0, frontY, frontDepth - 0.035);
        guide.renderOrder = 1000;
        guide.userData.kind = 'selection-front-guide';
        group.add(guide);
        this.selectionFrontGuide = guide;

        const marker = this.makeLabelSprite('FRONT', '#111111', '#ffffff');
        marker.position.set(0, frontY + 0.075, frontDepth);
        marker.scale.set(0.30, 0.085, 1);
        marker.material.depthTest = false;
        marker.material.depthWrite = false;
        marker.renderOrder = 1001;
        group.add(marker);
        this.selectionFrontMarker = marker;
      }

      const selectedUnit = (this.currentData?.units || []).find(item => String(item.uid) === String(uid));
      if (!selectedUnit?.locked) {
        const bounds = new this.THREE.Box3().setFromObject(group);
        const worldTop = new this.THREE.Vector3(
          (bounds.min.x + bounds.max.x) / 2,
          bounds.max.y + 0.13,
          (bounds.min.z + bounds.max.z) / 2
        );
        group.worldToLocal(worldTop);
        const moveHandle = this.makeMoveHandleSprite(uid);
        moveHandle.position.copy(worldTop);
        group.add(moveHandle);
        this.selectionMoveHandle = moveHandle;
      }
    }

    makeMoveHandleSprite(uid) {
      const THREE = this.THREE;
      const canvas = document.createElement('canvas');
      canvas.width = 192;
      canvas.height = 192;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, 192, 192);

      ctx.beginPath();
      ctx.arc(96, 96, 78, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,.96)';
      ctx.fill();
      ctx.lineWidth = 8;
      ctx.strokeStyle = '#55bde1';
      ctx.stroke();

      ctx.strokeStyle = '#55bde1';
      ctx.fillStyle = '#55bde1';
      ctx.lineWidth = 10;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(96, 39);
      ctx.lineTo(96, 153);
      ctx.moveTo(39, 96);
      ctx.lineTo(153, 96);
      ctx.stroke();

      const arrows = [
        [[96, 29], [80, 49], [112, 49]],
        [[96, 163], [80, 143], [112, 143]],
        [[29, 96], [49, 80], [49, 112]],
        [[163, 96], [143, 80], [143, 112]]
      ];
      arrows.forEach(points => {
        ctx.beginPath();
        ctx.moveTo(points[0][0], points[0][1]);
        ctx.lineTo(points[1][0], points[1][1]);
        ctx.lineTo(points[2][0], points[2][1]);
        ctx.closePath();
        ctx.fill();
      });

      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false
      });
      const sprite = new THREE.Sprite(material);
      sprite.scale.set(0.22, 0.22, 1);
      sprite.renderOrder = 1100;
      sprite.userData.kind = 'move-handle';
      sprite.userData.unitId = String(uid);
      return sprite;
    }

    setPointerRay(event) {
      if (!this.renderer || !this.camera || !this.raycaster || !this.pointer) return false;
      const rect = this.renderer.domElement.getBoundingClientRect();
      if (!rect.width || !rect.height) return false;
      this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      this.raycaster.setFromCamera(this.pointer, this.camera);
      return true;
    }

    startMoveDrag(event) {
      if (!this.selectionMoveHandle || !this.selectedUid || !this.setPointerRay(event)) return false;
      const hit = this.raycaster.intersectObject(this.selectionMoveHandle, true)[0];
      if (!hit) return false;

      const unit = (this.currentData?.units || []).find(item => String(item.uid) === this.selectedUid);
      const product = unit && (this.currentData?.productById?.get
        ? this.currentData.productById.get(String(unit.productId))
        : (this.currentData?.catalog || []).find(item => String(item.id) === String(unit.productId)));
      const group = this.groupsByUnit.get(this.selectedUid);
      if (!unit || !product || !group || unit.locked) return false;

      const floorPoint = new this.THREE.Vector3();
      if (!this.raycaster.ray.intersectPlane(this.dragPlane, floorPoint)) return false;

      const started = this.onMove(this.selectedUid, Number(unit.x) || 0, Number(unit.y) || 0, 'start');
      if (started?.allowed === false) return false;

      this.moveDrag = {
        uid: this.selectedUid,
        pointerId: event.pointerId,
        group,
        unit,
        product,
        offsetX: group.position.x - floorPoint.x,
        offsetZ: group.position.z - floorPoint.z,
        x: Number(unit.x) || 0,
        y: Number(unit.y) || 0,
        originX: Number(unit.x) || 0,
        originY: Number(unit.y) || 0,
        startFloorX: floorPoint.x,
        startFloorZ: floorPoint.z
      };
      this.pointerDown = null;
      if (this.controls) this.controls.enabled = false;
      try { this.renderer.domElement.setPointerCapture?.(event.pointerId); } catch (_) {}
      this.renderer.domElement.classList.add('is-moving-unit');
      event.preventDefault();
      event.stopPropagation();
      return true;
    }

    handleMoveDrag(event) {
      const drag = this.moveDrag;
      if (!drag || drag.pointerId !== event.pointerId || !this.setPointerRay(event)) return;

      const floorPoint = new this.THREE.Vector3();
      if (!this.raycaster.ray.intersectPlane(this.dragPlane, floorPoint)) return;

      const roomWidth = Number(this.currentData?.roomWidth || 4000);
      const roomDepth = Number(this.currentData?.roomDepth || 3000);
      const rect = this.unitRect(drag.unit, drag.product);
      const dragSensitivity = 0.55;
      const rawX = drag.originX + (floorPoint.x - drag.startFloorX) * 1000 * dragSensitivity;
      const rawY = drag.originY + (floorPoint.z - drag.startFloorZ) * 1000 * dragSensitivity;
      const moved = this.onMove(drag.uid, rawX, rawY, 'move') || {};
      if (moved.allowed === false) return;

      drag.x = Number.isFinite(Number(moved.x)) ? Number(moved.x) : rawX;
      drag.y = Number.isFinite(Number(moved.y)) ? Number(moved.y) : rawY;
      drag.group.position.x = (drag.x + rect.width / 2 - roomWidth / 2) / 1000;
      drag.group.position.z = (drag.y + rect.depth / 2 - roomDepth / 2) / 1000;
      this.selectionHelper?.update?.();

      event.preventDefault();
      event.stopPropagation();
    }

    finishMoveDrag(event, cancelled = false) {
      const drag = this.moveDrag;
      if (!drag || (event?.pointerId !== undefined && drag.pointerId !== event.pointerId)) return;
      this.moveDrag = null;
      this.renderer?.domElement?.classList.remove('is-moving-unit');
      if (this.controls) this.controls.enabled = true;
      try { this.renderer?.domElement?.releasePointerCapture?.(drag.pointerId); } catch (_) {}
      this.onMove(drag.uid, drag.x, drag.y, cancelled ? 'cancel' : 'end');
      event?.preventDefault?.();
      event?.stopPropagation?.();
    }

    handlePick(event) {
      if (!this.pointerDown || !this.renderer || !this.camera) return;
      const dx = event.clientX - this.pointerDown.x;
      const dy = event.clientY - this.pointerDown.y;
      this.pointerDown = null;
      if (Math.hypot(dx, dy) > 7) return;

      if (!this.setPointerRay(event)) return;

      const hits = this.raycaster.intersectObjects([...this.groupsByUnit.values()], true);
      const hit = hits.find(item => {
        let node = item.object;
        while (node) {
          if (node.userData?.unitId) return true;
          node = node.parent;
        }
        return false;
      });

      if (!hit) {
        this.onSelect(null);
        return;
      }

      let node = hit.object;
      while (node && !node.userData?.unitId) node = node.parent;
      const uid = node?.userData?.unitId || null;
      if (uid) this.onSelect(uid);
    }

    keepRoomVisible() {
      if (!this.currentData || !this.camera || !this.modelGroup) return;
      const roomW = Number(this.currentData.roomWidth || 4000) / 1000;
      const roomD = Number(this.currentData.roomDepth || 3000) / 1000;
      const margin = 0.035;

      this.modelGroup.traverse(object => {
        const edge = object.userData?.wallEdge;
        if (!edge) return;

        // Whichever solid wall the camera moves behind becomes an open cutaway.
        // The room therefore stays visible even at the limits of touch orbiting.
        const behindBack = edge === 'back' && this.camera.position.z > roomD / 2 + margin;
        const behindLeft = edge === 'left' && this.camera.position.x < -roomW / 2 - margin;
        const behindRight = edge === 'right' && this.camera.position.x > roomW / 2 + margin;
        object.visible = !(behindBack || behindLeft || behindRight);
      });
    }

    setPresetWallVisibility() {
      this.keepRoomVisible();
    }

    screenNudgeDirection(direction) {
      if (!this.ready || !this.camera) return direction;
      const THREE = this.THREE;
      const horizontal = direction === 'left' || direction === 'right';
      const vector = horizontal
        ? new THREE.Vector3(1, 0, 0)
        : new THREE.Vector3(0, 1, 0);
      vector.applyQuaternion(this.camera.quaternion);
      vector.y = 0;

      // A top-down camera can leave its projected up vector extremely small.
      // Use the projected viewing direction as a stable fallback.
      if (vector.lengthSq() < 0.0001) {
        this.camera.getWorldDirection(vector);
        vector.y = 0;
      }
      if (vector.lengthSq() < 0.0001) return direction;
      vector.normalize();

      const reverse = direction === 'left' || direction === 'down';
      if (reverse) vector.multiplyScalar(-1);

      // Cabinet plan coordinates map directly to world X/Z. Snap the screen
      // direction to the closest plan axis so each press remains exactly 10/50mm.
      if (Math.abs(vector.x) >= Math.abs(vector.z)) {
        return vector.x >= 0 ? 'right' : 'left';
      }
      return vector.z >= 0 ? 'down' : 'up';
    }

    cameraPreset(name) {
      if (!this.ready || !this.currentData) return;
      const roomW = Number(this.currentData.roomWidth || 4000) / 1000;
      const roomD = Number(this.currentData.roomDepth || 3000) / 1000;
      const maxSide = Math.max(roomW, roomD, 2.8);
      const target = new this.THREE.Vector3(0, Math.min(1.1, this.roomHeight * 0.42), 0);

      if (name === 'front') {
        this.camera.position.set(0, Math.min(1.55, this.roomHeight * 0.64), -roomD / 2 - maxSide * 0.95);
      } else if (name === 'back') {
        this.camera.position.set(0, Math.min(1.55, this.roomHeight * 0.64), roomD / 2 + maxSide * 0.95);
      } else if (name === 'top') {
        this.camera.position.set(0.001, maxSide * 1.55 + this.roomHeight, 0.001);
      } else if (name === 'left') {
        this.camera.position.set(roomW / 2 + maxSide * 1.05, 1.45, 0);
      } else {
        this.camera.position.set(maxSide * 0.82, Math.max(2.2, this.roomHeight * 1.05), -maxSide * 0.95);
      }

      this.controls.target.copy(target);
      this.controls.update();
      this.keepRoomVisible();
    }

    fitView() {
      this.cameraPreset('perspective');
    }

    destroy() {
      cancelAnimationFrame(this.frame);
      this.resizeObserver?.disconnect?.();
      this.controls?.dispose?.();
      this.disposeObject(this.modelGroup);
      this.renderer?.dispose?.();
      this.renderer?.domElement?.remove?.();
      this.ready = false;
    }
  }

  window.JDKitchen3D = JDKitchen3D;
})();
